import {
  DEFAULT_TTL,
  hasOwn
} from "./chunk-MB3DEFRC.js";
import {
  applyChange,
  assert,
  assertArray,
  assertBoolean,
  assertNumber,
  assertObject,
  assertString,
  compareValues,
  drainStreams,
  must,
  normalizeUndefined,
  throwInvalidType,
  unreachable,
  valuesEqual
} from "./chunk-2NCGIK6G.js";
import {
  __export,
  __reExport
} from "./chunk-424PT5DM.js";

// ../shared/src/valita.ts
var valita_exports = {};
__export(valita_exports, {
  assert: () => assert2,
  deepPartial: () => deepPartial,
  instanceOfAbstractType: () => instanceOfAbstractType,
  is: () => is,
  parse: () => parse,
  readonly: () => readonly,
  readonlyArray: () => readonlyArray,
  readonlyObject: () => readonlyObject,
  readonlyRecord: () => readonlyRecord,
  test: () => test,
  testOptional: () => testOptional
});
__reExport(valita_exports, valita_star);
import * as v from "@badrap/valita";
import * as valita_star from "@badrap/valita";
function toDisplay(value) {
  switch (typeof value) {
    case "string":
    case "number":
    case "boolean":
      return JSON.stringify(value);
    case "undefined":
      return "undefined";
    case "bigint":
      return value.toString() + "n";
    default:
      if (value === null) {
        return "null";
      }
      if (Array.isArray(value)) {
        return "array";
      }
      return typeof value;
  }
}
function toDisplayAtPath(v2, path2) {
  if (!path2?.length) {
    return toDisplay(v2);
  }
  let cur = v2;
  for (const p of path2) {
    cur = cur[p];
  }
  return toDisplay(cur);
}
function displayList(word, expected, toDisplay2 = (x) => String(x)) {
  if (expected.length === 1) {
    return toDisplay2(expected[0]);
  }
  const suffix = `${toDisplay2(
    expected[expected.length - 2]
  )} ${word} ${toDisplay2(expected[expected.length - 1])}`;
  if (expected.length === 2) {
    return suffix;
  }
  return `${expected.slice(0, -2).map(toDisplay2).join(", ")}, ${suffix}`;
}
function getMessage(err2, v2, schema, mode) {
  const firstIssue = err2.issues[0];
  const { path: path2 } = firstIssue;
  const atPath = path2?.length ? ` at ${path2.join(".")}` : "";
  switch (firstIssue.code) {
    case "invalid_type":
      return `Expected ${displayList(
        "or",
        firstIssue.expected
      )}${atPath}. Got ${toDisplayAtPath(v2, path2)}`;
    case "missing_value": {
      const atPath2 = path2 && path2.length > 1 ? ` at ${path2.slice(0, -1).join(".")}` : "";
      if (firstIssue.path?.length) {
        return `Missing property ${firstIssue.path.at(-1)}${atPath2}`;
      }
      return `TODO Unknown missing property${atPath2}`;
    }
    case "invalid_literal":
      return `Expected literal value ${displayList(
        "or",
        firstIssue.expected,
        toDisplay
      )}${atPath} Got ${toDisplayAtPath(v2, path2)}`;
    case "invalid_length": {
      return `Expected array with length ${firstIssue.minLength === firstIssue.maxLength ? firstIssue.minLength : `between ${firstIssue.minLength} and ${firstIssue.maxLength}`}${atPath}. Got array with length ${v2.length}`;
    }
    case "unrecognized_keys":
      if (firstIssue.keys.length === 1) {
        return `Unexpected property ${firstIssue.keys[0]}${atPath}`;
      }
      return `Unexpected properties ${displayList(
        "and",
        firstIssue.keys
      )}${atPath}`;
    case "invalid_union":
      return schema.name === "union" ? getDeepestUnionParseError(v2, schema, mode ?? "strict") : `Invalid union value${atPath}`;
    case "custom_error": {
      const { error } = firstIssue;
      const message = !error ? "unknown" : typeof error === "string" ? error : error.message ?? "unknown";
      return `${message}${atPath}. Got ${toDisplayAtPath(v2, path2)}`;
    }
  }
}
function getDeepestUnionParseError(value, schema, mode) {
  const failures = [];
  for (const type of schema.options) {
    const r = type.try(value, { mode });
    if (!r.ok) {
      failures.push({ type, err: r });
    }
  }
  if (failures.length) {
    failures.sort(pathCmp);
    if (failures.length === 1 || pathCmp(failures[0], failures[1]) < 0) {
      return getMessage(failures[0].err, value, failures[0].type, mode);
    }
  }
  try {
    const str = JSON.stringify(value);
    return `Invalid union value: ${str}`;
  } catch (e) {
    return `Invalid union value`;
  }
}
function pathCmp(a, b) {
  const aPath = a.err.issues[0].path;
  const bPath = b.err.issues[0].path;
  if (aPath.length !== bPath.length) {
    return bPath.length - aPath.length;
  }
  for (let i = 0; i < aPath.length; i++) {
    if (bPath[i] > aPath[i]) {
      return -1;
    }
    if (bPath[i] < aPath[i]) {
      return 1;
    }
  }
  return 0;
}
function parse(value, schema, mode) {
  const res = test(value, schema, mode);
  if (!res.ok) {
    throw new TypeError(res.error);
  }
  return res.value;
}
function is(value, schema, mode) {
  return test(value, schema, mode).ok;
}
function assert2(value, schema, mode) {
  parse(value, schema, mode);
}
function test(value, schema, mode) {
  const res = schema.try(value, mode ? { mode } : void 0);
  if (!res.ok) {
    return {
      ok: false,
      error: getMessage(res, value, schema, mode)
    };
  }
  return res;
}
function testOptional(value, schema, mode) {
  let flags = 1;
  if (mode === "passthrough") {
    flags = 0;
  } else if (mode === "strip") {
    flags = 2;
  }
  const res = schema.func(value, flags);
  if (res === void 0) {
    return { ok: true, value };
  } else if (res.ok) {
    return res;
  }
  const err2 = new v.ValitaError(res);
  return { ok: false, error: getMessage(err2, value, schema, mode) };
}
function readonly(t) {
  return t;
}
function readonlyObject(t) {
  return v.object(t);
}
function readonlyArray(t) {
  return v.array(t);
}
function readonlyRecord(t) {
  return v.record(t);
}
var AbstractType = Object.getPrototypeOf(
  Object.getPrototypeOf(v.string().optional())
).constructor;
function instanceOfAbstractType(obj) {
  return obj instanceof AbstractType;
}
function deepPartial(s) {
  const shape = {};
  for (const [key, type] of Object.entries(s.shape)) {
    if (type.name === "object") {
      shape[key] = deepPartial(type).optional();
    } else {
      shape[key] = type.optional();
    }
  }
  return v.object(shape);
}

// ../replicache/src/dag/store.ts
var ChunkNotFoundError = class extends Error {
  name = "ChunkNotFoundError";
  hash;
  constructor(hash2) {
    super(`Chunk not found ${hash2}`);
    this.hash = hash2;
  }
};
async function mustGetChunk(store, hash2) {
  const chunk = await store.getChunk(hash2);
  if (chunk) {
    return chunk;
  }
  throw new ChunkNotFoundError(hash2);
}
async function mustGetHeadHash(name, store) {
  const hash2 = await store.getHead(name);
  assert(hash2, `Missing head ${name}`);
  return hash2;
}

// ../replicache/src/format-version-enum.ts
var DD31 = 5;
var V6 = 6;
var V7 = 7;
var Latest = V7;

// ../shared/src/config.ts
var isProd = process.env.NODE_ENV === "production";

// ../shared/src/json.ts
function deepEqual(a, b) {
  if (a === b) {
    return true;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  switch (typeof a) {
    case "boolean":
    case "number":
    case "string":
      return false;
  }
  a = a;
  if (Array.isArray(a)) {
    if (!Array.isArray(b)) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) {
        return false;
      }
    }
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  if (Array.isArray(b)) {
    return false;
  }
  a = a;
  b = b;
  let aSize = 0;
  for (const key in a) {
    if (hasOwn(a, key)) {
      if (!deepEqual(a[key], b[key])) {
        return false;
      }
      aSize++;
    }
  }
  let bSize = 0;
  for (const key in b) {
    if (hasOwn(b, key)) {
      bSize++;
    }
  }
  return aSize === bSize;
}
function assertJSONValue(v2) {
  if (isProd) {
    return;
  }
  switch (typeof v2) {
    case "boolean":
    case "number":
    case "string":
      return;
    case "object":
      if (v2 === null) {
        return;
      }
      if (Array.isArray(v2)) {
        return assertJSONArray(v2);
      }
      return assertObjectIsJSONObject(v2);
  }
  throwInvalidType(v2, "JSON value");
}
function assertJSONObject(v2) {
  assertObject(v2);
  assertObjectIsJSONObject(v2);
}
function assertObjectIsJSONObject(v2) {
  for (const k in v2) {
    if (hasOwn(v2, k)) {
      const value = v2[k];
      if (value !== void 0) {
        assertJSONValue(value);
      }
    }
  }
}
function assertJSONArray(v2) {
  for (const item of v2) {
    assertJSONValue(item);
  }
}
function isJSONValue(v2, path2) {
  switch (typeof v2) {
    case "boolean":
    case "number":
    case "string":
      return true;
    case "object":
      if (v2 === null) {
        return true;
      }
      if (Array.isArray(v2)) {
        return isJSONArray(v2, path2);
      }
      return objectIsJSONObject(v2, path2);
  }
  return false;
}
function isJSONObject(v2, path2) {
  if (typeof v2 !== "object" || v2 === null) {
    return false;
  }
  return objectIsJSONObject(v2, path2);
}
function objectIsJSONObject(v2, path2) {
  for (const k in v2) {
    if (hasOwn(v2, k)) {
      path2.push(k);
      const value = v2[k];
      if (value !== void 0 && !isJSONValue(value, path2)) {
        return false;
      }
      path2.pop();
    }
  }
  return true;
}
function isJSONArray(v2, path2) {
  for (let i = 0; i < v2.length; i++) {
    path2.push(i);
    if (!isJSONValue(v2[i], path2)) {
      return false;
    }
    path2.pop();
  }
  return true;
}

// ../shared/src/random-uint64.ts
function randomUint64() {
  const high = Math.floor(Math.random() * 4294967295);
  const low = Math.floor(Math.random() * 4294967295);
  return BigInt(high) << 32n | BigInt(low);
}

// ../replicache/src/hash.ts
var STRING_LENGTH = 22;
var hashRe = /^[0-9a-v-]+$/;
var emptyUUID = "0".repeat(STRING_LENGTH);
var emptyHash = emptyUUID;
var newRandomHash = makeNewRandomHashFunctionInternal();
function toStringAndSlice(n, len) {
  return n.toString(32).slice(-len).padStart(len, "0");
}
function makeNewRandomHashFunctionInternal() {
  let base = "";
  let i = 0;
  return () => {
    if (!base) {
      base = toStringAndSlice(randomUint64(), 12);
    }
    const tail = toStringAndSlice(i++, 10);
    return base + tail;
  };
}
function isHash(value) {
  return typeof value === "string" && hashRe.test(value);
}
function assertHash(value) {
  assert2(value, hashSchema);
}
var hashSchema = valita_exports.string().assert(isHash, "Invalid hash");

// ../replicache/src/size-of-value.ts
var SIZE_TAG = 1;
var SIZE_INT32 = 4;
var SIZE_SMI = 5;
var SIZE_DOUBLE = 8;
function getSizeOfValue(value) {
  switch (typeof value) {
    case "string":
      return SIZE_TAG + SIZE_INT32 + value.length;
    case "number":
      if (isSmi(value)) {
        if (value <= -(2 ** 30) || value >= 2 ** 30 - 1) {
          return SIZE_TAG + SIZE_SMI;
        }
        return SIZE_TAG + SIZE_INT32;
      }
      return SIZE_TAG + SIZE_DOUBLE;
    case "boolean":
      return SIZE_TAG;
    case "object":
      if (value === null) {
        return SIZE_TAG;
      }
      if (Array.isArray(value)) {
        let sum = 2 * SIZE_TAG + SIZE_INT32;
        for (const element of value) {
          sum += getSizeOfValue(element);
        }
        return sum;
      }
      {
        const val = value;
        let sum = 2 * SIZE_TAG + SIZE_INT32;
        for (const k in val) {
          if (hasOwn(val, k)) {
            const propertyValue = val[k];
            if (propertyValue !== void 0) {
              sum += getSizeOfValue(k) + getSizeOfValue(propertyValue);
            }
          }
        }
        return sum;
      }
  }
  throw new Error(`Invalid value. type: ${typeof value}, value: ${value}`);
}
function isSmi(value) {
  return value === (value | 0);
}
var entryFixed = 2 * SIZE_TAG + SIZE_INT32 + SIZE_TAG + SIZE_INT32;
function getSizeOfEntry(key, value) {
  return entryFixed + getSizeOfValue(key) + getSizeOfValue(value);
}

// ../replicache/src/btree/node.ts
import { compareUTF8 } from "compare-utf8";

// ../shared/src/iterables.ts
function* joinIterables(...iters) {
  for (const iter of iters) {
    yield* iter;
  }
}
function* filterIter(iter, p) {
  let index = 0;
  for (const t of iter) {
    if (p(t, index++)) {
      yield t;
    }
  }
}
function* mapIter(iter, f) {
  let index = 0;
  for (const t of iter) {
    yield f(t, index++);
  }
}
var IterWrapper = class _IterWrapper {
  iter;
  constructor(iter) {
    this.iter = iter;
  }
  [Symbol.iterator]() {
    return this.iter[Symbol.iterator]();
  }
  map(f) {
    return new _IterWrapper(mapIter(this.iter, f));
  }
  filter(p) {
    return new _IterWrapper(filterIter(this.iter, p));
  }
};
function wrapIterable(iter) {
  return new IterWrapper(iter);
}
function* mergeIterables(iterables, comparator, distinct = false) {
  const iterators = iterables.map((i) => i[Symbol.iterator]());
  try {
    const current = iterators.map((i) => i.next());
    let lastYielded;
    while (current.some((c) => !c.done)) {
      const min = current.reduce(
        (acc, c, i) => {
          if (c.done) {
            return acc;
          }
          if (acc === void 0 || comparator(c.value, acc[0]) < 0) {
            return [c.value, i];
          }
          return acc;
        },
        void 0
      );
      assert(min !== void 0, "min is undefined");
      current[min[1]] = iterators[min[1]].next();
      if (lastYielded !== void 0 && distinct && comparator(lastYielded, min[0]) === 0) {
        continue;
      }
      lastYielded = min[0];
      yield min[0];
    }
  } finally {
    for (const it of iterators) {
      it.return?.();
    }
  }
}

// ../replicache/src/binary-search.ts
function binarySearch(high, compare) {
  let low = 0;
  while (low < high) {
    const mid = low + (high - low >> 1);
    const i = compare(mid);
    if (i === 0) {
      return mid;
    }
    if (i > 0) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

// ../replicache/src/frozen-json.ts
var deepFrozenObjects = /* @__PURE__ */ new WeakSet();
function deepFreeze(v2) {
  if (isProd) {
    return v2;
  }
  deepFreezeInternal(v2, []);
  return v2;
}
function deepFreezeInternal(v2, seen) {
  switch (typeof v2) {
    case "undefined":
      throw new TypeError("Unexpected value undefined");
    case "boolean":
    case "number":
    case "string":
      return;
    case "object": {
      if (v2 === null) {
        return;
      }
      if (deepFrozenObjects.has(v2)) {
        return;
      }
      deepFrozenObjects.add(v2);
      if (seen.includes(v2)) {
        throwInvalidType(v2, "Cyclic JSON object");
      }
      seen.push(v2);
      Object.freeze(v2);
      if (Array.isArray(v2)) {
        deepFreezeArray(v2, seen);
      } else {
        deepFreezeObject(v2, seen);
      }
      seen.pop();
      return;
    }
    default:
      throwInvalidType(v2, "JSON value");
  }
}
function deepFreezeArray(v2, seen) {
  for (const item of v2) {
    deepFreezeInternal(item, seen);
  }
}
function deepFreezeObject(v2, seen) {
  for (const k in v2) {
    if (hasOwn(v2, k)) {
      const value = v2[k];
      if (value !== void 0) {
        deepFreezeInternal(value, seen);
      }
    }
  }
}
function assertDeepFrozen(v2) {
  if (isProd) {
    return;
  }
  if (!isDeepFrozen(v2, [])) {
    throw new Error("Expected frozen object");
  }
}
function isDeepFrozen(v2, seen) {
  switch (typeof v2) {
    case "boolean":
    case "number":
    case "string":
      return true;
    case "object":
      if (v2 === null) {
        return true;
      }
      if (deepFrozenObjects.has(v2)) {
        return true;
      }
      if (!Object.isFrozen(v2)) {
        return false;
      }
      if (seen.includes(v2)) {
        throwInvalidType(v2, "Cyclic JSON object");
      }
      seen.push(v2);
      if (Array.isArray(v2)) {
        for (const item of v2) {
          if (!isDeepFrozen(item, seen)) {
            seen.pop();
            return false;
          }
        }
      } else {
        for (const k in v2) {
          if (hasOwn(v2, k)) {
            const value = v2[k];
            if (value !== void 0 && !isDeepFrozen(value, seen)) {
              seen.pop();
              return false;
            }
          }
        }
      }
      deepFrozenObjects.add(v2);
      seen.pop();
      return true;
    default:
      throwInvalidType(v2, "JSON value");
  }
}
function deepFreezeAllowUndefined(v2) {
  if (v2 === void 0) {
    return void 0;
  }
  return deepFreeze(v2);
}

// ../replicache/src/btree/node.ts
var NODE_LEVEL = 0;
var NODE_ENTRIES = 1;
function makeNodeChunkData(level, entries, formatVersion) {
  return deepFreeze([
    level,
    formatVersion >= V7 ? entries : entries.map((e) => e.slice(0, 2))
  ]);
}
async function findLeaf(key, hash2, source, expectedRootHash) {
  const node = await source.getNode(hash2);
  if (expectedRootHash !== source.rootHash) {
    return findLeaf(key, source.rootHash, source, source.rootHash);
  }
  if (isDataNodeImpl(node)) {
    return node;
  }
  const { entries } = node;
  let i = binarySearch2(key, entries);
  if (i === entries.length) {
    i--;
  }
  const entry = entries[i];
  return findLeaf(key, entry[1], source, expectedRootHash);
}
function binarySearch2(key, entries) {
  return binarySearch(
    entries.length,
    (i) => compareUTF8(key, entries[i][0])
  );
}
function binarySearchFound(i, entries, key) {
  return i !== entries.length && entries[i][0] === key;
}
function parseBTreeNode(v2, formatVersion, getSizeOfEntry2) {
  if (isProd && formatVersion >= V7) {
    return v2;
  }
  assertArray(v2);
  assertDeepFrozen(v2);
  assert(v2.length >= 2);
  const [level, entries] = v2;
  assertNumber(level);
  assertArray(entries);
  const f = level > 0 ? assertString : assertJSONValue;
  if (formatVersion >= V7) {
    for (const e of entries) {
      assertEntry(e, f);
    }
    return v2;
  }
  const newEntries = entries.map((e) => convertNonV7Entry(e, f, getSizeOfEntry2));
  return [level, newEntries];
}
function assertEntry(entry, f) {
  assertArray(entry);
  assert(entry.length >= 3);
  assertString(entry[0]);
  f(entry[1]);
  assertNumber(entry[2]);
}
function convertNonV7Entry(entry, f, getSizeOfEntry2) {
  assertArray(entry);
  assert(entry.length >= 2);
  assertString(entry[0]);
  f(entry[1]);
  const entrySize = getSizeOfEntry2(entry[0], entry[1]);
  return [entry[0], entry[1], entrySize];
}
var NodeImpl = class {
  entries;
  hash;
  isMutable;
  #childNodeSize = -1;
  constructor(entries, hash2, isMutable) {
    this.entries = entries;
    this.hash = hash2;
    this.isMutable = isMutable;
  }
  maxKey() {
    return this.entries[this.entries.length - 1][0];
  }
  getChildNodeSize(tree) {
    if (this.#childNodeSize !== -1) {
      return this.#childNodeSize;
    }
    let sum = tree.chunkHeaderSize;
    for (const entry of this.entries) {
      sum += entry[2];
    }
    return this.#childNodeSize = sum;
  }
  _updateNode(tree) {
    this.#childNodeSize = -1;
    tree.updateNode(
      this
    );
  }
};
function toChunkData(node, formatVersion) {
  return makeNodeChunkData(node.level, node.entries, formatVersion);
}
var DataNodeImpl = class extends NodeImpl {
  level = 0;
  set(key, value, entrySize, tree) {
    let deleteCount;
    const i = binarySearch2(key, this.entries);
    if (!binarySearchFound(i, this.entries, key)) {
      deleteCount = 0;
    } else {
      deleteCount = 1;
    }
    return Promise.resolve(
      this.#splice(tree, i, deleteCount, [key, value, entrySize])
    );
  }
  #splice(tree, start, deleteCount, ...items) {
    if (this.isMutable) {
      this.entries.splice(start, deleteCount, ...items);
      this._updateNode(tree);
      return this;
    }
    const entries = readonlySplice(this.entries, start, deleteCount, ...items);
    return tree.newDataNodeImpl(entries);
  }
  del(key, tree) {
    const i = binarySearch2(key, this.entries);
    if (!binarySearchFound(i, this.entries, key)) {
      return Promise.resolve(this);
    }
    return Promise.resolve(this.#splice(tree, i, 1));
  }
  async *keys(_tree) {
    for (const entry of this.entries) {
      yield entry[0];
    }
  }
  async *entriesIter(_tree) {
    for (const entry of this.entries) {
      yield entry;
    }
  }
};
function readonlySplice(array5, start, deleteCount, ...items) {
  const arr = array5.slice(0, start);
  for (let i = 0; i < items.length; i++) {
    arr.push(items[i]);
  }
  for (let i = start + deleteCount; i < array5.length; i++) {
    arr.push(array5[i]);
  }
  return arr;
}
var InternalNodeImpl = class _InternalNodeImpl extends NodeImpl {
  level;
  constructor(entries, hash2, level, isMutable) {
    super(entries, hash2, isMutable);
    this.level = level;
  }
  async set(key, value, entrySize, tree) {
    let i = binarySearch2(key, this.entries);
    if (i === this.entries.length) {
      i--;
    }
    const childHash = this.entries[i][1];
    const oldChildNode = await tree.getNode(childHash);
    const childNode = await oldChildNode.set(key, value, entrySize, tree);
    const childNodeSize = childNode.getChildNodeSize(tree);
    if (childNodeSize > tree.maxSize || childNodeSize < tree.minSize) {
      return this.#mergeAndPartition(tree, i, childNode);
    }
    const newEntry = createNewInternalEntryForNode(
      childNode,
      tree.getEntrySize
    );
    return this.#replaceChild(tree, i, newEntry);
  }
  /**
   * This merges the child node entries with previous or next sibling and then
   * partitions the merged entries.
   */
  async #mergeAndPartition(tree, i, childNode) {
    const level = this.level - 1;
    const thisEntries = this.entries;
    let values;
    let startIndex;
    let removeCount;
    if (i > 0) {
      const hash2 = thisEntries[i - 1][1];
      const previousSibling = await tree.getNode(hash2);
      values = joinIterables(
        previousSibling.entries,
        childNode.entries
      );
      startIndex = i - 1;
      removeCount = 2;
    } else if (i < thisEntries.length - 1) {
      const hash2 = thisEntries[i + 1][1];
      const nextSibling = await tree.getNode(hash2);
      values = joinIterables(
        childNode.entries,
        nextSibling.entries
      );
      startIndex = i;
      removeCount = 2;
    } else {
      values = childNode.entries;
      startIndex = i;
      removeCount = 1;
    }
    const partitions = partition(
      values,
      (value) => value[2],
      tree.minSize - tree.chunkHeaderSize,
      tree.maxSize - tree.chunkHeaderSize
    );
    const newEntries = [];
    for (const entries2 of partitions) {
      const node = tree.newNodeImpl(entries2, level);
      const newHashEntry = createNewInternalEntryForNode(
        node,
        tree.getEntrySize
      );
      newEntries.push(newHashEntry);
    }
    if (this.isMutable) {
      this.entries.splice(startIndex, removeCount, ...newEntries);
      this._updateNode(tree);
      return this;
    }
    const entries = readonlySplice(
      thisEntries,
      startIndex,
      removeCount,
      ...newEntries
    );
    return tree.newInternalNodeImpl(entries, this.level);
  }
  #replaceChild(tree, index, newEntry) {
    if (this.isMutable) {
      this.entries.splice(index, 1, newEntry);
      this._updateNode(tree);
      return this;
    }
    const entries = readonlySplice(this.entries, index, 1, newEntry);
    return tree.newInternalNodeImpl(entries, this.level);
  }
  async del(key, tree) {
    const i = binarySearch2(key, this.entries);
    if (i === this.entries.length) {
      return this;
    }
    const childHash = this.entries[i][1];
    const oldChildNode = await tree.getNode(childHash);
    const oldHash = oldChildNode.hash;
    const childNode = await oldChildNode.del(key, tree);
    if (childNode.hash === oldHash) {
      return this;
    }
    if (childNode.entries.length === 0) {
      const entries = readonlySplice(this.entries, i, 1);
      return tree.newInternalNodeImpl(entries, this.level);
    }
    if (i === 0 && this.entries.length === 1) {
      return childNode;
    }
    if (childNode.getChildNodeSize(tree) > tree.minSize) {
      const entry = createNewInternalEntryForNode(childNode, tree.getEntrySize);
      return this.#replaceChild(tree, i, entry);
    }
    return this.#mergeAndPartition(tree, i, childNode);
  }
  async *keys(tree) {
    for (const entry of this.entries) {
      const childNode = await tree.getNode(entry[1]);
      yield* childNode.keys(tree);
    }
  }
  async *entriesIter(tree) {
    for (const entry of this.entries) {
      const childNode = await tree.getNode(entry[1]);
      yield* childNode.entriesIter(tree);
    }
  }
  getChildren(start, length, tree) {
    const ps = [];
    for (let i = start; i < length && i < this.entries.length; i++) {
      ps.push(tree.getNode(this.entries[i][1]));
    }
    return Promise.all(ps);
  }
  async getCompositeChildren(start, length, tree) {
    const { level } = this;
    if (length === 0) {
      return new _InternalNodeImpl([], newRandomHash(), level - 1, true);
    }
    const output = await this.getChildren(start, start + length, tree);
    if (level > 1) {
      const entries2 = [];
      for (const child of output) {
        entries2.push(...child.entries);
      }
      return new _InternalNodeImpl(entries2, newRandomHash(), level - 1, true);
    }
    assert(level === 1);
    const entries = [];
    for (const child of output) {
      entries.push(...child.entries);
    }
    return new DataNodeImpl(entries, newRandomHash(), true);
  }
};
function newNodeImpl(entries, hash2, level, isMutable) {
  if (level === 0) {
    return new DataNodeImpl(
      entries,
      hash2,
      isMutable
    );
  }
  return new InternalNodeImpl(entries, hash2, level, isMutable);
}
function isDataNodeImpl(node) {
  return node.level === 0;
}
function partition(values, getSizeOfEntry2, min, max) {
  const partitions = [];
  const sizes = [];
  let sum = 0;
  let accum = [];
  for (const value of values) {
    const size = getSizeOfEntry2(value);
    if (size >= max) {
      if (accum.length > 0) {
        partitions.push(accum);
        sizes.push(sum);
      }
      partitions.push([value]);
      sizes.push(size);
      sum = 0;
      accum = [];
    } else if (sum + size >= min) {
      accum.push(value);
      partitions.push(accum);
      sizes.push(sum + size);
      sum = 0;
      accum = [];
    } else {
      sum += size;
      accum.push(value);
    }
  }
  if (sum > 0) {
    if (sizes.length > 0 && sum + sizes[sizes.length - 1] <= max) {
      partitions[partitions.length - 1].push(...accum);
    } else {
      partitions.push(accum);
    }
  }
  return partitions;
}
var emptyDataNode = makeNodeChunkData(
  0,
  [],
  Latest
);
var emptyDataNodeImpl = new DataNodeImpl([], emptyHash, false);
function createNewInternalEntryForNode(node, getSizeOfEntry2) {
  const key = node.maxKey();
  const value = node.hash;
  const size = getSizeOfEntry2(key, value);
  return [key, value, size];
}

// ../replicache/src/btree/splice.ts
var SPLICE_UNASSIGNED = -1;
var SPLICE_AT = 0;
var SPLICE_REMOVED = 1;
var SPLICE_ADDED = 2;
var SPLICE_FROM = 3;
var KEY = 0;
var VALUE = 1;
function* computeSplices(previous, current) {
  let previousIndex = 0;
  let currentIndex = 0;
  let splice;
  function ensureAssigned(splice2, index) {
    if (splice2[SPLICE_FROM] === SPLICE_UNASSIGNED) {
      splice2[SPLICE_FROM] = index;
    }
  }
  function newSplice() {
    return [previousIndex, 0, 0, SPLICE_UNASSIGNED];
  }
  while (previousIndex < previous.length && currentIndex < current.length) {
    if (previous[previousIndex][KEY] === current[currentIndex][KEY]) {
      if (deepEqual(
        // These are really Hash | InternalValue
        previous[previousIndex][VALUE],
        current[currentIndex][VALUE]
      )) {
        if (splice) {
          ensureAssigned(splice, 0);
          yield splice;
          splice = void 0;
        }
      } else {
        if (!splice) {
          splice = newSplice();
        }
        splice[SPLICE_ADDED]++;
        splice[SPLICE_REMOVED]++;
        ensureAssigned(splice, currentIndex);
      }
      previousIndex++;
      currentIndex++;
    } else if (previous[previousIndex][KEY] < current[currentIndex][KEY]) {
      if (!splice) {
        splice = newSplice();
      }
      splice[SPLICE_REMOVED]++;
      previousIndex++;
    } else {
      if (!splice) {
        splice = newSplice();
      }
      splice[SPLICE_ADDED]++;
      ensureAssigned(splice, currentIndex);
      currentIndex++;
    }
  }
  if (currentIndex < current.length) {
    if (!splice) {
      splice = newSplice();
    }
    splice[SPLICE_ADDED] += current.length - currentIndex;
    ensureAssigned(splice, currentIndex);
  }
  if (previousIndex < previous.length) {
    if (!splice) {
      splice = newSplice();
    }
    splice[SPLICE_REMOVED] += previous.length - previousIndex;
  }
  if (splice) {
    ensureAssigned(splice, 0);
    yield splice;
  }
}

// ../replicache/src/btree/read.ts
var NODE_HEADER_SIZE = 11;
var BTreeRead = class {
  _cache = /* @__PURE__ */ new Map();
  _dagRead;
  _formatVersion;
  rootHash;
  getEntrySize;
  chunkHeaderSize;
  constructor(dagRead, formatVersion, root = emptyHash, getEntrySize = getSizeOfEntry, chunkHeaderSize = NODE_HEADER_SIZE) {
    this._dagRead = dagRead;
    this._formatVersion = formatVersion;
    this.rootHash = root;
    this.getEntrySize = getEntrySize;
    this.chunkHeaderSize = chunkHeaderSize;
  }
  async getNode(hash2) {
    if (hash2 === emptyHash) {
      return emptyDataNodeImpl;
    }
    const cached = this._cache.get(hash2);
    if (cached) {
      return cached;
    }
    const chunk = await this._dagRead.mustGetChunk(hash2);
    const data = parseBTreeNode(
      chunk.data,
      this._formatVersion,
      this.getEntrySize
    );
    const impl = newNodeImpl(
      data[NODE_ENTRIES],
      hash2,
      data[NODE_LEVEL],
      false
    );
    this._cache.set(hash2, impl);
    return impl;
  }
  async get(key) {
    const leaf = await findLeaf(key, this.rootHash, this, this.rootHash);
    const index = binarySearch2(key, leaf.entries);
    if (!binarySearchFound(index, leaf.entries, key)) {
      return void 0;
    }
    return leaf.entries[index][1];
  }
  async has(key) {
    const leaf = await findLeaf(key, this.rootHash, this, this.rootHash);
    const index = binarySearch2(key, leaf.entries);
    return binarySearchFound(index, leaf.entries, key);
  }
  async isEmpty() {
    const { rootHash } = this;
    const node = await this.getNode(this.rootHash);
    if (this.rootHash !== rootHash) {
      return this.isEmpty();
    }
    return node.entries.length === 0;
  }
  // We don't do any encoding of the key in the map, so we have no way of
  // determining from an entry.key alone whether it is a regular key or an
  // encoded IndexKey in an index map. Without encoding regular map keys the
  // caller has to deal with encoding and decoding the keys for the index map.
  scan(fromKey) {
    return scanForHash(
      this.rootHash,
      () => this.rootHash,
      this.rootHash,
      fromKey,
      async (hash2) => {
        const cached = await this.getNode(hash2);
        if (cached) {
          return [
            cached.level,
            cached.isMutable ? cached.entries.slice() : cached.entries
          ];
        }
        const chunk = await this._dagRead.mustGetChunk(hash2);
        return parseBTreeNode(
          chunk.data,
          this._formatVersion,
          this.getEntrySize
        );
      }
    );
  }
  async *keys() {
    const node = await this.getNode(this.rootHash);
    yield* node.keys(this);
  }
  async *entries() {
    const node = await this.getNode(this.rootHash);
    yield* node.entriesIter(this);
  }
  [Symbol.asyncIterator]() {
    return this.entries();
  }
  async *diff(last) {
    const [currentNode, lastNode] = await Promise.all([
      this.getNode(this.rootHash),
      last.getNode(last.rootHash)
    ]);
    yield* diffNodes(lastNode, currentNode, last, this);
  }
};
async function* diffNodes(last, current, lastTree, currentTree) {
  if (last.level > current.level) {
    const lastChild = await last.getCompositeChildren(
      0,
      last.entries.length,
      lastTree
    );
    yield* diffNodes(lastChild, current, lastTree, currentTree);
    return;
  }
  if (current.level > last.level) {
    const currentChild = await current.getCompositeChildren(
      0,
      current.entries.length,
      currentTree
    );
    yield* diffNodes(last, currentChild, lastTree, currentTree);
    return;
  }
  if (isDataNodeImpl(last) && isDataNodeImpl(current)) {
    yield* diffEntries(
      last.entries,
      current.entries
    );
    return;
  }
  const initialSplices = computeSplices(
    last.entries,
    current.entries
  );
  for (const splice of initialSplices) {
    const [lastChild, currentChild] = await Promise.all([
      last.getCompositeChildren(
        splice[SPLICE_AT],
        splice[SPLICE_REMOVED],
        lastTree
      ),
      current.getCompositeChildren(
        splice[SPLICE_FROM],
        splice[SPLICE_ADDED],
        currentTree
      )
    ]);
    yield* diffNodes(lastChild, currentChild, lastTree, currentTree);
  }
}
function* diffEntries(lastEntries, currentEntries) {
  const lastLength = lastEntries.length;
  const currentLength = currentEntries.length;
  let i = 0;
  let j = 0;
  while (i < lastLength && j < currentLength) {
    const lastKey = lastEntries[i][0];
    const currentKey = currentEntries[j][0];
    if (lastKey === currentKey) {
      if (!deepEqual(lastEntries[i][1], currentEntries[j][1])) {
        yield {
          op: "change",
          key: lastKey,
          oldValue: lastEntries[i][1],
          newValue: currentEntries[j][1]
        };
      }
      i++;
      j++;
    } else if (lastKey < currentKey) {
      yield {
        op: "del",
        key: lastKey,
        oldValue: lastEntries[i][1]
      };
      i++;
    } else {
      yield {
        op: "add",
        key: currentKey,
        newValue: currentEntries[j][1]
      };
      j++;
    }
  }
  for (; i < lastLength; i++) {
    yield {
      op: "del",
      key: lastEntries[i][0],
      oldValue: lastEntries[i][1]
    };
  }
  for (; j < currentLength; j++) {
    yield {
      op: "add",
      key: currentEntries[j][0],
      newValue: currentEntries[j][1]
    };
  }
}
async function* scanForHash(expectedRootHash, getRootHash, hash2, fromKey, readNode) {
  if (hash2 === emptyHash) {
    return;
  }
  const data = await readNode(hash2);
  const entries = data[NODE_ENTRIES];
  let i = 0;
  if (fromKey) {
    i = binarySearch2(fromKey, entries);
  }
  if (data[NODE_LEVEL] > 0) {
    for (; i < entries.length; i++) {
      yield* scanForHash(
        expectedRootHash,
        getRootHash,
        entries[i][1],
        fromKey,
        readNode
      );
      fromKey = "";
    }
  } else {
    for (; i < entries.length; i++) {
      const rootHash = getRootHash();
      if (expectedRootHash !== rootHash) {
        yield* scanForHash(
          rootHash,
          getRootHash,
          rootHash,
          entries[i][0],
          readNode
        );
        return;
      }
      yield entries[i];
    }
  }
}
async function allEntriesAsDiff(map, op) {
  const diff3 = [];
  const make = op === "add" ? (entry) => ({
    op: "add",
    key: entry[0],
    newValue: entry[1]
  }) : (entry) => ({
    op: "del",
    key: entry[0],
    oldValue: entry[1]
  });
  for await (const entry of map.entries()) {
    diff3.push(make(entry));
  }
  return diff3;
}

// ../shared/src/string-compare.ts
function stringCompare(a, b) {
  if (a === b) {
    return 0;
  }
  if (a < b) {
    return -1;
  }
  return 1;
}

// ../replicache/src/cookies.ts
function compareCookies(a, b) {
  if (a === b) {
    return 0;
  }
  if (a === null) {
    return -1;
  }
  if (b === null) {
    return 1;
  }
  const cva = getCompareValue(a);
  const cvb = getCompareValue(b);
  if (typeof cva === "string" || typeof cvb === "string") {
    return stringCompare(String(cva), String(cvb));
  }
  return cva - cvb;
}
function getCompareValue(cookie) {
  if (typeof cookie === "string" || typeof cookie === "number") {
    return cookie;
  }
  return cookie.order;
}
function assertCookie(v2) {
  if (v2 === null || typeof v2 === "string" || typeof v2 === "number") {
    return;
  }
  assertJSONObject(v2);
  if (typeof v2.order === "string" || typeof v2.order === "number") {
    return;
  }
  throw new Error("Invalid cookie");
}

// ../replicache/src/dag/chunk.ts
function asRefs(sortedRefs) {
  return sortedRefs;
}
function toRefs(refs) {
  if (Array.isArray(refs)) {
    refs.sort();
    for (let i = 1; i < refs.length; i++) {
      assert(refs[i - 1] !== refs[i], "Refs must not have duplicates");
    }
    return asRefs(refs);
  }
  const refsArray = [...refs];
  refsArray.sort();
  return asRefs(refsArray);
}
var Chunk = class {
  hash;
  data;
  /**
   * Meta is an array of refs. If there are no refs we do not write a meta
   * chunk.
   */
  meta;
  constructor(hash2, data, refs) {
    assert(
      !refs.includes(hash2),
      "Chunk cannot reference itself"
    );
    assertDeepFrozen(data);
    this.hash = hash2;
    this.data = data;
    this.meta = refs;
  }
};
function assertRefs(v2) {
  if (!Array.isArray(v2)) {
    throw new Error("Refs must be an array");
  }
  if (v2.length > 0) {
    assertString(v2[0]);
    for (let i = 1; i < v2.length; i++) {
      assertString(v2[i]);
    }
  }
}
function createChunk(data, refs, chunkHasher) {
  const hash2 = chunkHasher();
  return new Chunk(hash2, data, refs);
}

// ../replicache/src/db/meta-type-enum.ts
var LocalDD31 = 4;
var SnapshotDD31 = 5;

// ../replicache/src/db/commit.ts
var DEFAULT_HEAD_NAME = "main";
function commitIsLocalDD31(commit) {
  return isLocalMetaDD31(commit.meta);
}
function commitIsLocal(commit) {
  return commitIsLocalDD31(commit);
}
function commitIsSnapshot(commit) {
  return isSnapshotMetaDD31(commit.meta);
}
var Commit = class {
  chunk;
  constructor(chunk) {
    this.chunk = chunk;
  }
  get meta() {
    return this.chunk.data.meta;
  }
  get valueHash() {
    return this.chunk.data.valueHash;
  }
  getMutationID(clientID, dagRead) {
    return getMutationID(clientID, dagRead, this.meta);
  }
  async getNextMutationID(clientID, dagRead) {
    return await this.getMutationID(clientID, dagRead) + 1;
  }
  get indexes() {
    return this.chunk.data.indexes;
  }
};
async function getMutationID(clientID, dagRead, meta) {
  switch (meta.type) {
    case SnapshotDD31:
      return meta.lastMutationIDs[clientID] ?? 0;
    case LocalDD31: {
      if (meta.clientID === clientID) {
        return meta.mutationID;
      }
      const { basisHash } = meta;
      const basisCommit = await commitFromHash(basisHash, dagRead);
      return getMutationID(clientID, dagRead, basisCommit.meta);
    }
    default:
      unreachable(meta);
  }
}
async function localMutations(fromCommitHash, dagRead) {
  const commits = await commitChain(fromCommitHash, dagRead);
  return commits.filter((c) => commitIsLocal(c));
}
async function localMutationsDD31(fromCommitHash, dagRead) {
  const commits = await commitChain(fromCommitHash, dagRead);
  return commits.filter((c) => commitIsLocalDD31(c));
}
async function localMutationsGreaterThan(commit, mutationIDLimits, dagRead) {
  const commits = [];
  const remainingMutationIDLimits = new Map(Object.entries(mutationIDLimits));
  while (!commitIsSnapshot(commit) && remainingMutationIDLimits.size > 0) {
    if (commitIsLocalDD31(commit)) {
      const { meta } = commit;
      const mutationIDLowerLimit = remainingMutationIDLimits.get(meta.clientID);
      if (mutationIDLowerLimit !== void 0) {
        if (meta.mutationID <= mutationIDLowerLimit) {
          remainingMutationIDLimits.delete(meta.clientID);
        } else {
          commits.push(commit);
        }
      }
    }
    const { basisHash } = commit.meta;
    if (basisHash === null) {
      throw new Error(`Commit ${commit.chunk.hash} has no basis`);
    }
    commit = await commitFromHash(basisHash, dagRead);
  }
  return commits;
}
async function baseSnapshotFromHead(name, dagRead) {
  const hash2 = await dagRead.getHead(name);
  assert(hash2, `Missing head ${name}`);
  return baseSnapshotFromHash(hash2, dagRead);
}
async function baseSnapshotHashFromHash(hash2, dagRead) {
  return (await baseSnapshotFromHash(hash2, dagRead)).chunk.hash;
}
async function baseSnapshotFromHash(hash2, dagRead) {
  const commit = await commitFromHash(hash2, dagRead);
  return baseSnapshotFromCommit(commit, dagRead);
}
async function baseSnapshotFromCommit(commit, dagRead) {
  while (!commitIsSnapshot(commit)) {
    const { meta } = commit;
    if (isLocalMetaDD31(meta)) {
      commit = await commitFromHash(meta.baseSnapshotHash, dagRead);
    } else {
      const { basisHash } = meta;
      if (basisHash === null) {
        throw new Error(`Commit ${commit.chunk.hash} has no basis`);
      }
      commit = await commitFromHash(basisHash, dagRead);
    }
  }
  return commit;
}
function snapshotMetaParts(c, clientID) {
  const m = c.meta;
  const lmid = m.lastMutationIDs[clientID] ?? 0;
  return [lmid, m.cookieJSON];
}
function compareCookiesForSnapshots(a, b) {
  return compareCookies(a.meta.cookieJSON, b.meta.cookieJSON);
}
async function commitChain(fromCommitHash, dagRead) {
  let commit = await commitFromHash(fromCommitHash, dagRead);
  const commits = [];
  while (!commitIsSnapshot(commit)) {
    const { meta } = commit;
    const { basisHash } = meta;
    if (basisHash === null) {
      throw new Error(`Commit ${commit.chunk.hash} has no basis`);
    }
    commits.push(commit);
    commit = await commitFromHash(basisHash, dagRead);
  }
  commits.push(commit);
  return commits;
}
async function commitFromHash(hash2, dagRead) {
  const chunk = await dagRead.mustGetChunk(hash2);
  return fromChunk(chunk);
}
async function commitFromHead(name, dagRead) {
  const hash2 = await mustGetHeadHash(name, dagRead);
  return commitFromHash(hash2, dagRead);
}
function assertLocalMetaDD31(v2) {
  assertString(v2.clientID);
  assertNumber(v2.mutationID);
  assertString(v2.mutatorName);
  if (!v2.mutatorName) {
    throw new Error("Missing mutator name");
  }
  assertJSONValue(v2.mutatorArgsJSON);
  if (v2.originalHash !== null) {
    assertHash(v2.originalHash);
  }
  assertNumber(v2.timestamp);
}
function isLocalMetaDD31(meta) {
  return meta.type === LocalDD31;
}
function assertSnapshotMetaDD31(v2) {
  if (v2.basisHash !== null) {
    assertHash(v2.basisHash);
  }
  assertJSONValue(v2.cookieJSON);
  assertLastMutationIDs(v2.lastMutationIDs);
}
function assertLastMutationIDs(v2) {
  assertObject(v2);
  for (const e of Object.values(v2)) {
    assertNumber(e);
  }
}
function assertSnapshotCommitDD31(c) {
  assertSnapshotMetaDD31(c.meta);
}
function isSnapshotMetaDD31(meta) {
  return meta.type === SnapshotDD31;
}
function assertMeta(v2) {
  assertObject(v2);
  assertDeepFrozen(v2);
  if (v2.basisHash !== null) {
    assertString(v2.basisHash);
  }
  assertNumber(v2.type);
  switch (v2.type) {
    case LocalDD31:
      assertLocalMetaDD31(v2);
      break;
    case SnapshotDD31:
      assertSnapshotMetaDD31(v2);
      break;
    default:
      throw new Error(`Invalid enum value ${v2.type}`);
  }
}
function chunkIndexDefinitionEqualIgnoreName(a, b) {
  return a.jsonPointer === b.jsonPointer && (a.allowEmpty ?? false) === (b.allowEmpty ?? false) && a.keyPrefix === b.keyPrefix;
}
function assertChunkIndexDefinition(v2) {
  assertObject(v2);
  assertDeepFrozen(v2);
  assertString(v2.name);
  assertString(v2.keyPrefix);
  assertString(v2.jsonPointer);
  if (v2.allowEmpty !== void 0) {
    assertBoolean(v2.allowEmpty);
  }
}
function toChunkIndexDefinition(name, indexDefinition) {
  return {
    name,
    keyPrefix: indexDefinition.prefix ?? "",
    jsonPointer: indexDefinition.jsonPointer,
    allowEmpty: indexDefinition.allowEmpty ?? false
  };
}
function assertIndexRecord(v2) {
  assertObject(v2);
  assertDeepFrozen(v2);
  assertChunkIndexDefinition(v2.definition);
  assertString(v2.valueHash);
}
function assertIndexRecords(v2) {
  assertArray(v2);
  assertDeepFrozen(v2);
  for (const ir of v2) {
    assertIndexRecord(ir);
  }
}
function newLocalDD31(createChunk2, basisHash, baseSnapshotHash, mutationID, mutatorName, mutatorArgsJSON, originalHash, valueHash, indexes, timestamp, clientID) {
  const meta = {
    type: LocalDD31,
    basisHash,
    baseSnapshotHash,
    mutationID,
    mutatorName,
    mutatorArgsJSON,
    originalHash,
    timestamp,
    clientID
  };
  return commitFromCommitData(
    createChunk2,
    makeCommitData(meta, valueHash, indexes)
  );
}
function newSnapshotDD31(createChunk2, basisHash, lastMutationIDs, cookieJSON, valueHash, indexes) {
  return commitFromCommitData(
    createChunk2,
    newSnapshotCommitDataDD31(
      basisHash,
      lastMutationIDs,
      cookieJSON,
      valueHash,
      indexes
    )
  );
}
function newSnapshotCommitDataDD31(basisHash, lastMutationIDs, cookieJSON, valueHash, indexes) {
  const meta = {
    type: SnapshotDD31,
    basisHash,
    lastMutationIDs,
    cookieJSON
  };
  return makeCommitData(meta, valueHash, indexes);
}
function fromChunk(chunk) {
  validateChunk(chunk);
  return new Commit(chunk);
}
function commitFromCommitData(createChunk2, data) {
  return new Commit(createChunk2(data, getRefs(data)));
}
function getRefs(data) {
  const refs = /* @__PURE__ */ new Set();
  refs.add(data.valueHash);
  const { meta } = data;
  switch (meta.type) {
    case LocalDD31:
      meta.basisHash && refs.add(meta.basisHash);
      break;
    case SnapshotDD31:
      break;
    default:
      unreachable(meta);
  }
  for (const index of data.indexes) {
    refs.add(index.valueHash);
  }
  return toRefs(refs);
}
function makeCommitData(meta, valueHash, indexes) {
  return deepFreeze({
    meta,
    valueHash,
    indexes
  });
}
function assertCommitData(v2) {
  if (isProd) {
    return;
  }
  assertObject(v2);
  assertDeepFrozen(v2);
  assertMeta(v2.meta);
  assertString(v2.valueHash);
  assertIndexRecords(v2.indexes);
}
function validateChunk(chunk) {
  const { data } = chunk;
  assertCommitData(data);
  const seen = /* @__PURE__ */ new Set();
  for (const index of data.indexes) {
    const { name } = index.definition;
    if (seen.has(name)) {
      throw new Error(`Duplicate index ${name}`);
    }
    seen.add(name);
  }
}

// ../replicache/src/db/index-operation-enum.ts
var Add = 0;
var Remove = 1;

// ../replicache/src/db/index.ts
var IndexRead = class {
  meta;
  map;
  constructor(meta, map) {
    this.meta = meta;
    this.map = map;
  }
};
var IndexWrite = class extends IndexRead {
  // Note: does not update self.meta.valueHash (doesn't need to at this point as flush
  // is only called during commit.)
  flush() {
    return this.map.flush();
  }
  clear() {
    return this.map.clear();
  }
};
async function indexValue(lc, index, op, key, val, jsonPointer, allowEmpty) {
  try {
    for (const entry of getIndexKeys(key, val, jsonPointer, allowEmpty)) {
      switch (op) {
        case Add:
          await index.put(entry, val);
          break;
        case Remove:
          await index.del(entry);
          break;
      }
    }
  } catch (e) {
    lc.info?.("Not indexing value", val, ":", e);
  }
}
function getIndexKeys(primary, value, jsonPointer, allowEmpty) {
  const target = evaluateJSONPointer(value, jsonPointer);
  if (target === void 0) {
    if (allowEmpty) {
      return [];
    }
    throw new Error(`No value at path: ${jsonPointer}`);
  }
  const values = Array.isArray(target) ? target : [target];
  const indexKeys = [];
  for (const value2 of values) {
    if (typeof value2 === "string") {
      indexKeys.push(encodeIndexKey([value2, primary]));
    } else {
      throw new Error("Unsupported target type");
    }
  }
  return indexKeys;
}
var KEY_VERSION_0 = "\0";
var KEY_SEPARATOR = "\0";
function encodeIndexKey(indexKey) {
  const secondary = indexKey[0];
  const primary = indexKey[1];
  if (secondary.includes("\0")) {
    throw new Error("Secondary key cannot contain null byte");
  }
  return KEY_VERSION_0 + secondary + KEY_SEPARATOR + primary;
}
function encodeIndexScanKey(secondary, primary) {
  const k = encodeIndexKey([secondary, primary || ""]);
  if (primary === void 0) {
    return k.slice(0, k.length - 1);
  }
  return k;
}
function decodeIndexKey(encodedIndexKey) {
  if (encodedIndexKey[0] !== KEY_VERSION_0) {
    throw new Error("Invalid version");
  }
  const versionLen = KEY_VERSION_0.length;
  const separatorLen = KEY_SEPARATOR.length;
  const separatorOffset = encodedIndexKey.indexOf(KEY_SEPARATOR, versionLen);
  if (separatorOffset === -1) {
    throw new Error("Invalid formatting");
  }
  const secondary = encodedIndexKey.slice(versionLen, separatorOffset);
  const primary = encodedIndexKey.slice(separatorOffset + separatorLen);
  return [secondary, primary];
}
function evaluateJSONPointer(value, pointer) {
  function parseIndex(s) {
    if (s.startsWith("+") || s.startsWith("0") && s.length !== 1) {
      return void 0;
    }
    return parseInt(s, 10);
  }
  if (pointer === "") {
    return value;
  }
  if (!pointer.startsWith("/")) {
    throw new Error(`Invalid JSON pointer: ${pointer}`);
  }
  const tokens = pointer.split("/").slice(1).map((x) => x.replace(/~1/g, "/").replace(/~0/g, "~"));
  let target = value;
  for (const token of tokens) {
    let targetOpt;
    if (Array.isArray(target)) {
      const i = parseIndex(token);
      if (i === void 0) {
        return void 0;
      }
      targetOpt = target[i];
    } else if (target === null) {
      return void 0;
    } else if (typeof target === "object") {
      target = target;
      targetOpt = target[token];
    }
    if (targetOpt === void 0) {
      return void 0;
    }
    target = targetOpt;
  }
  return target;
}

// ../replicache/src/db/read.ts
var Read = class {
  #dagRead;
  map;
  indexes;
  constructor(dagRead, map, indexes) {
    this.#dagRead = dagRead;
    this.map = map;
    this.indexes = indexes;
  }
  has(key) {
    return this.map.has(key);
  }
  get(key) {
    return this.map.get(key);
  }
  isEmpty() {
    return this.map.isEmpty();
  }
  getMapForIndex(indexName) {
    const idx = this.indexes.get(indexName);
    if (idx === void 0) {
      throw new Error(`Unknown index name: ${indexName}`);
    }
    return idx.map;
  }
  get closed() {
    return this.#dagRead.closed;
  }
  close() {
    this.#dagRead.release();
  }
};
function readFromDefaultHead(dagRead, formatVersion) {
  return readFromHead(DEFAULT_HEAD_NAME, dagRead, formatVersion);
}
async function readFromHead(name, dagRead, formatVersion) {
  const commit = await commitFromHead(name, dagRead);
  return readFromCommit(commit, dagRead, formatVersion);
}
async function readFromHash(hash2, dagRead, formatVersion) {
  const commit = await commitFromHash(hash2, dagRead);
  return readFromCommit(commit, dagRead, formatVersion);
}
function readFromCommit(commit, dagRead, formatVersion) {
  const indexes = readIndexesForRead(commit, dagRead, formatVersion);
  const map = new BTreeRead(dagRead, formatVersion, commit.valueHash);
  return new Read(dagRead, map, indexes);
}
function readIndexesForRead(commit, dagRead, formatVersion) {
  const m = /* @__PURE__ */ new Map();
  for (const index of commit.indexes) {
    m.set(
      index.definition.name,
      new IndexRead(
        index,
        new BTreeRead(dagRead, formatVersion, index.valueHash)
      )
    );
  }
  return m;
}

// ../replicache/src/index-defs.ts
var indexDefinitionSchema = readonlyObject({
  prefix: valita_exports.string().optional(),
  jsonPointer: valita_exports.string(),
  allowEmpty: valita_exports.boolean().optional()
});
var indexDefinitionsSchema = readonlyRecord(
  indexDefinitionSchema
);
function indexDefinitionEqual(a, b) {
  return a.jsonPointer === b.jsonPointer && (a.allowEmpty ?? false) === (b.allowEmpty ?? false) && (a.prefix ?? "") === (b.prefix ?? "");
}
function indexDefinitionsEqual(a, b) {
  if (Object.keys(a).length !== Object.keys(b).length) {
    return false;
  }
  for (const [aKey, aValue] of Object.entries(a)) {
    const bValue = b[aKey];
    if (!bValue || !indexDefinitionEqual(aValue, bValue)) {
      return false;
    }
  }
  return true;
}

// ../replicache/src/persist/client-groups.ts
var clientGroupSchema = readonlyObject({
  /**
   * The hash of the commit in the perdag last persisted to this client group.
   * Should only be updated by clients assigned to this client group.
   */
  headHash: hashSchema,
  /**
   * Set of mutator names common to all clients assigned to this client group.
   */
  mutatorNames: readonlyArray(valita_exports.string()),
  /**
   * Index definitions common to all clients assigned to this client group.
   */
  indexes: indexDefinitionsSchema,
  /**
   * The highest mutation ID of every client assigned to this client group.
   * Should only be updated by clients assigned to this client group. Read by
   * other clients to determine if there are unacknowledged pending mutations
   * for them to try to recover. This is redundant with information in the
   * commit graph at `headHash`, but allows other clients to determine if there
   * are unacknowledged pending mutations without having to load the commit
   * graph.
   */
  mutationIDs: readonlyRecord(valita_exports.number()),
  /**
   * The highest lastMutationID received from the server for every client
   * assigned to this client group.
   *
   * Should be updated by the clients assigned to this client group whenever
   * they persist to this client group. Read by other clients to determine if
   * there are unacknowledged pending mutations for them to recover and
   * *updated* by other clients upon successfully recovering pending mutations
   * to avoid redundant pushes of pending mutations.
   *
   * Note: This will be the same as the `lastMutationIDs` of the base snapshot
   * of the client group's commit graph when written by clients assigned to this
   * client group.  However, when written by another client recovering mutations
   * it may be different because the other client does not update the commit
   * graph.
   */
  lastServerAckdMutationIDs: valita_exports.record(valita_exports.number()),
  /**
   * If the server deletes this client group it can signal that the client group
   * was deleted. If that happens we mark this client group as disabled so that
   * we do not use it again when creating new clients.
   */
  disabled: valita_exports.boolean()
});
var CLIENT_GROUPS_HEAD_NAME = "client-groups";
function assertClientGroup(value) {
  assert2(value, clientGroupSchema);
}
function chunkDataToClientGroupMap(chunkData) {
  assertObject(chunkData);
  const clientGroups = /* @__PURE__ */ new Map();
  for (const [key, value] of Object.entries(chunkData)) {
    if (value !== void 0) {
      assertClientGroup(value);
      clientGroups.set(key, value);
    }
  }
  return clientGroups;
}
function clientGroupMapToChunkData(clientGroups, dagWrite) {
  const chunkData = {};
  for (const [clientGroupID, clientGroup] of clientGroups.entries()) {
    dagWrite.assertValidHash(clientGroup.headHash);
    chunkData[clientGroupID] = {
      ...clientGroup,
      mutatorNames: [...clientGroup.mutatorNames.values()]
    };
  }
  return deepFreeze(chunkData);
}
async function getClientGroupsAtHash(hash2, dagRead) {
  const chunk = await dagRead.getChunk(hash2);
  return chunkDataToClientGroupMap(chunk?.data);
}
async function getClientGroups(dagRead) {
  const hash2 = await dagRead.getHead(CLIENT_GROUPS_HEAD_NAME);
  if (!hash2) {
    return /* @__PURE__ */ new Map();
  }
  return getClientGroupsAtHash(hash2, dagRead);
}
async function setClientGroups(clientGroups, dagWrite) {
  const currClientGroups = await getClientGroups(dagWrite);
  for (const [clientGroupID, clientGroup] of clientGroups) {
    const currClientGroup = currClientGroups.get(clientGroupID);
    validateClientGroupUpdate(clientGroup, currClientGroup);
  }
  return setValidatedClientGroups(clientGroups, dagWrite);
}
async function setClientGroup(clientGroupID, clientGroup, dagWrite) {
  const currClientGroups = await getClientGroups(dagWrite);
  const currClientGroup = currClientGroups.get(clientGroupID);
  validateClientGroupUpdate(clientGroup, currClientGroup);
  const newClientGroups = new Map(currClientGroups);
  newClientGroups.set(clientGroupID, clientGroup);
  return setValidatedClientGroups(newClientGroups, dagWrite);
}
function validateClientGroupUpdate(clientGroup, currClientGroup) {
  const mutatorNamesSet = new Set(clientGroup.mutatorNames);
  assert(
    mutatorNamesSet.size === clientGroup.mutatorNames.length,
    "A client group's mutatorNames must be a set."
  );
  if (currClientGroup !== void 0) {
    assert(
      indexDefinitionsEqual(currClientGroup.indexes, clientGroup.indexes),
      "A client group's index definitions must never change."
    );
    assert(
      mutatorNamesEqual(mutatorNamesSet, currClientGroup.mutatorNames),
      "A client group's mutatorNames must never change."
    );
  }
}
async function setValidatedClientGroups(clientGroups, dagWrite) {
  const chunkData = clientGroupMapToChunkData(clientGroups, dagWrite);
  const refs = /* @__PURE__ */ new Set();
  for (const clientGroup of clientGroups.values()) {
    refs.add(clientGroup.headHash);
  }
  const chunk = dagWrite.createChunk(chunkData, toRefs(refs));
  await dagWrite.putChunk(chunk);
  await dagWrite.setHead(CLIENT_GROUPS_HEAD_NAME, chunk.hash);
  return clientGroups;
}
function mutatorNamesEqual(mutatorNamesSet, mutatorNames) {
  if (mutatorNames.length !== mutatorNamesSet.size) {
    return false;
  }
  for (const mutatorName of mutatorNames) {
    if (!mutatorNamesSet.has(mutatorName)) {
      return false;
    }
  }
  return true;
}
async function getClientGroup(id, dagRead) {
  const clientGroups = await getClientGroups(dagRead);
  return clientGroups.get(id);
}
function clientGroupHasPendingMutations(clientGroup) {
  for (const [clientID, mutationID] of Object.entries(
    clientGroup.mutationIDs
  )) {
    const lastServerAckdMutationID = clientGroup.lastServerAckdMutationIDs[clientID];
    if (lastServerAckdMutationID === void 0 && mutationID !== 0 || lastServerAckdMutationID < mutationID) {
      return true;
    }
  }
  return false;
}
async function disableClientGroup(clientGroupID, dagWrite) {
  const clientGroup = await getClientGroup(clientGroupID, dagWrite);
  if (!clientGroup) {
    return;
  }
  const disabledClientGroup = {
    ...clientGroup,
    disabled: true
  };
  await setClientGroup(clientGroupID, disabledClientGroup, dagWrite);
}

// ../replicache/src/with-transactions.ts
function withRead(store, fn) {
  return using(store.read(), fn);
}
function withWriteNoImplicitCommit(store, fn) {
  return using(store.write(), fn);
}
function withWrite(store, fn) {
  return using(store.write(), async (write) => {
    const result = await fn(write);
    await write.commit();
    return result;
  });
}
async function using(x, fn) {
  const write = await x;
  try {
    return await fn(write);
  } finally {
    write.release();
  }
}

// ../replicache/src/async-iterable-to-array.ts
async function asyncIterableToArray(it) {
  const arr = [];
  for await (const v2 of it) {
    arr.push(v2);
  }
  return arr;
}

// ../replicache/src/btree/diff.ts
function diff(oldMap, newMap) {
  return asyncIterableToArray(newMap.diff(oldMap));
}

// ../replicache/src/btree/write.ts
import { Lock } from "@rocicorp/lock";
var BTreeWrite = class extends BTreeRead {
  /**
   * This rw lock is used to ensure we do not mutate the btree in parallel. It
   * would be a problem if we didn't have the lock in cases like this:
   *
   * ```ts
   * const p1 = tree.put('a', 0);
   * const p2 = tree.put('b', 1);
   * await p1;
   * await p2;
   * ```
   *
   * because both `p1` and `p2` would start from the old root hash but a put
   * changes the root hash so the two concurrent puts would lead to only one of
   * them actually working, and it is not deterministic which one would finish
   * last.
   */
  #lock = new Lock();
  #modified = /* @__PURE__ */ new Map();
  minSize;
  maxSize;
  constructor(dagWrite, formatVersion, root = emptyHash, minSize = 8 * 1024, maxSize = 16 * 1024, getEntrySize = getSizeOfEntry, chunkHeaderSize) {
    super(dagWrite, formatVersion, root, getEntrySize, chunkHeaderSize);
    this.minSize = minSize;
    this.maxSize = maxSize;
  }
  #addToModified(node) {
    assert(node.isMutable);
    this.#modified.set(node.hash, node);
    this._cache.set(node.hash, node);
  }
  updateNode(node) {
    assert(node.isMutable);
    this.#modified.delete(node.hash);
    node.hash = newRandomHash();
    this.#addToModified(node);
  }
  newInternalNodeImpl(entries, level) {
    const n = new InternalNodeImpl(entries, newRandomHash(), level, true);
    this.#addToModified(n);
    return n;
  }
  newDataNodeImpl(entries) {
    const n = new DataNodeImpl(entries, newRandomHash(), true);
    this.#addToModified(n);
    return n;
  }
  newNodeImpl(entries, level) {
    const n = newNodeImpl(entries, newRandomHash(), level, true);
    this.#addToModified(n);
    return n;
  }
  put(key, value) {
    return this.#lock.withLock(async () => {
      const oldRootNode = await this.getNode(this.rootHash);
      const entrySize = this.getEntrySize(key, value);
      const rootNode = await oldRootNode.set(key, value, entrySize, this);
      if (rootNode.getChildNodeSize(this) > this.maxSize) {
        const headerSize = this.chunkHeaderSize;
        const partitions = partition(
          rootNode.entries,
          (value2) => value2[2],
          this.minSize - headerSize,
          this.maxSize - headerSize
        );
        const { level } = rootNode;
        const entries = partitions.map((entries2) => {
          const node = this.newNodeImpl(entries2, level);
          return createNewInternalEntryForNode(node, this.getEntrySize);
        });
        const newRoot = this.newInternalNodeImpl(entries, level + 1);
        this.rootHash = newRoot.hash;
        return;
      }
      this.rootHash = rootNode.hash;
    });
  }
  del(key) {
    return this.#lock.withLock(async () => {
      const oldRootNode = await this.getNode(this.rootHash);
      const newRootNode = await oldRootNode.del(key, this);
      const found = this.rootHash !== newRootNode.hash;
      if (found) {
        if (newRootNode.level > 0 && newRootNode.entries.length === 1) {
          this.rootHash = newRootNode.entries[0][1];
        } else {
          this.rootHash = newRootNode.hash;
        }
      }
      return found;
    });
  }
  clear() {
    return this.#lock.withLock(() => {
      this.#modified.clear();
      this.rootHash = emptyHash;
    });
  }
  flush() {
    return this.#lock.withLock(async () => {
      const dagWrite = this._dagRead;
      if (this.rootHash === emptyHash) {
        const chunk = dagWrite.createChunk(emptyDataNode, []);
        await dagWrite.putChunk(chunk);
        return chunk.hash;
      }
      const newChunks = [];
      const newRoot = gatherNewChunks(
        this.rootHash,
        newChunks,
        dagWrite.createChunk,
        this.#modified,
        this._formatVersion
      );
      await Promise.all(newChunks.map((chunk) => dagWrite.putChunk(chunk)));
      this.#modified.clear();
      this.rootHash = newRoot;
      return newRoot;
    });
  }
};
function gatherNewChunks(hash2, newChunks, createChunk2, modified, formatVersion) {
  const node = modified.get(hash2);
  if (node === void 0) {
    return hash2;
  }
  if (isDataNodeImpl(node)) {
    const chunk2 = createChunk2(toChunkData(node, formatVersion), []);
    newChunks.push(chunk2);
    return chunk2.hash;
  }
  const refs = [];
  const { entries } = node;
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const childHash = entry[1];
    const newChildHash = gatherNewChunks(
      childHash,
      newChunks,
      createChunk2,
      modified,
      formatVersion
    );
    if (newChildHash !== childHash) {
      entries[i] = [entry[0], newChildHash, entry[2]];
    }
    refs.push(newChildHash);
  }
  const chunk = createChunk2(toChunkData(node, formatVersion), toRefs(refs));
  newChunks.push(chunk);
  return chunk.hash;
}

// ../replicache/src/lazy.ts
function lazy(factory) {
  let value;
  return () => {
    if (value === void 0) {
      value = factory();
    }
    return value;
  };
}

// ../replicache/src/sync/diff.ts
var DiffsMap = class extends Map {
  set(key, value) {
    if (value.length === 0) {
      return this;
    }
    return super.set(key, value);
  }
};
async function diff2(oldHash, newHash, read, diffConfig, formatVersion) {
  const [oldCommit, newCommit] = await Promise.all([
    commitFromHash(oldHash, read),
    commitFromHash(newHash, read)
  ]);
  return diffCommits(oldCommit, newCommit, read, diffConfig, formatVersion);
}
async function diffCommits(oldCommit, newCommit, read, diffConfig, formatVersion) {
  const diffsMap = new DiffsMap();
  if (!diffConfig.shouldComputeDiffs()) {
    return diffsMap;
  }
  const oldMap = new BTreeRead(read, formatVersion, oldCommit.valueHash);
  const newMap = new BTreeRead(read, formatVersion, newCommit.valueHash);
  const valueDiff = await diff(oldMap, newMap);
  diffsMap.set("", valueDiff);
  await addDiffsForIndexes(
    oldCommit,
    newCommit,
    read,
    diffsMap,
    diffConfig,
    formatVersion
  );
  return diffsMap;
}
async function addDiffsForIndexes(mainCommit, syncCommit, read, diffsMap, diffConfig, formatVersion) {
  const oldIndexes = readIndexesForRead(mainCommit, read, formatVersion);
  const newIndexes = readIndexesForRead(syncCommit, read, formatVersion);
  for (const [oldIndexName, oldIndex] of oldIndexes) {
    if (!diffConfig.shouldComputeDiffsForIndex(oldIndexName)) {
      continue;
    }
    const newIndex = newIndexes.get(oldIndexName);
    if (newIndex !== void 0) {
      assert(newIndex !== oldIndex);
      const diffs = await diff(oldIndex.map, newIndex.map);
      newIndexes.delete(oldIndexName);
      diffsMap.set(oldIndexName, diffs);
    } else {
      const diffs = await allEntriesAsDiff(oldIndex.map, "del");
      diffsMap.set(oldIndexName, diffs);
    }
  }
  for (const [newIndexName, newIndex] of newIndexes) {
    if (!diffConfig.shouldComputeDiffsForIndex(newIndexName)) {
      continue;
    }
    const diffs = await allEntriesAsDiff(newIndex.map, "add");
    diffsMap.set(newIndexName, diffs);
  }
}

// ../replicache/src/db/write.ts
var Write = class extends Read {
  #dagWrite;
  #basis;
  #meta;
  #clientID;
  #formatVersion;
  constructor(dagWrite, map, basis, meta, indexes, clientID, formatVersion) {
    super(dagWrite, map, indexes);
    this.#dagWrite = dagWrite;
    this.#basis = basis;
    this.#meta = meta;
    this.#clientID = clientID;
    this.#formatVersion = formatVersion;
    if (basis === void 0) {
      assert(meta.basisHash === emptyHash);
    } else {
      assert(meta.basisHash === basis.chunk.hash);
    }
  }
  /**
   * The value needs to be frozen since it is kept in memory and used later for
   * comparison as well as returned in `get`.
   */
  async put(lc, key, value) {
    const oldVal = lazy(() => this.map.get(key));
    await updateIndexes(lc, this.indexes, key, oldVal, value);
    await this.map.put(key, value);
  }
  getMutationID() {
    return getMutationID(this.#clientID, this.#dagWrite, this.#meta);
  }
  async del(lc, key) {
    const oldVal = lazy(() => this.map.get(key));
    if (oldVal !== void 0) {
      await updateIndexes(lc, this.indexes, key, oldVal, void 0);
    }
    return this.map.del(key);
  }
  async clear() {
    await this.map.clear();
    const ps = [];
    for (const idx of this.indexes.values()) {
      ps.push(idx.clear());
    }
    await Promise.all(ps);
  }
  async putCommit() {
    const valueHash = await this.map.flush();
    const indexRecords = [];
    for (const index of this.indexes.values()) {
      const valueHash2 = await index.flush();
      const indexRecord = {
        definition: index.meta.definition,
        valueHash: valueHash2
      };
      indexRecords.push(indexRecord);
    }
    let commit;
    const meta = this.#meta;
    switch (meta.type) {
      case LocalDD31: {
        assert(this.#formatVersion >= DD31);
        const {
          basisHash,
          mutationID,
          mutatorName,
          mutatorArgsJSON,
          originalHash,
          timestamp
        } = meta;
        commit = newLocalDD31(
          this.#dagWrite.createChunk,
          basisHash,
          await baseSnapshotHashFromHash(basisHash, this.#dagWrite),
          mutationID,
          mutatorName,
          mutatorArgsJSON,
          originalHash,
          valueHash,
          indexRecords,
          timestamp,
          this.#clientID
        );
        break;
      }
      case SnapshotDD31: {
        assert(this.#formatVersion > DD31);
        const { basisHash, lastMutationIDs, cookieJSON } = meta;
        commit = newSnapshotDD31(
          this.#dagWrite.createChunk,
          basisHash,
          lastMutationIDs,
          cookieJSON,
          valueHash,
          indexRecords
        );
        break;
      }
    }
    await this.#dagWrite.putChunk(commit.chunk);
    return commit;
  }
  // Return value is the hash of the new commit.
  async commit(headName) {
    const commit = await this.putCommit();
    const commitHash = commit.chunk.hash;
    await this.#dagWrite.setHead(headName, commitHash);
    await this.#dagWrite.commit();
    return commitHash;
  }
  async commitWithDiffs(headName, diffConfig) {
    const commit = this.putCommit();
    const diffMap = await this.#generateDiffs(diffConfig);
    const commitHash = (await commit).chunk.hash;
    await this.#dagWrite.setHead(headName, commitHash);
    await this.#dagWrite.commit();
    return [commitHash, diffMap];
  }
  async #generateDiffs(diffConfig) {
    const diffsMap = new DiffsMap();
    if (!diffConfig.shouldComputeDiffs()) {
      return diffsMap;
    }
    let valueDiff = [];
    if (this.#basis) {
      const basisMap = new BTreeRead(
        this.#dagWrite,
        this.#formatVersion,
        this.#basis.valueHash
      );
      valueDiff = await diff(basisMap, this.map);
    }
    diffsMap.set("", valueDiff);
    let basisIndexes;
    if (this.#basis) {
      basisIndexes = readIndexesForRead(
        this.#basis,
        this.#dagWrite,
        this.#formatVersion
      );
    } else {
      basisIndexes = /* @__PURE__ */ new Map();
    }
    for (const [name, index] of this.indexes) {
      if (!diffConfig.shouldComputeDiffsForIndex(name)) {
        continue;
      }
      const basisIndex = basisIndexes.get(name);
      assert(index !== basisIndex);
      const indexDiffResult = await (basisIndex ? diff(basisIndex.map, index.map) : (
        // No basis. All keys are new.
        allEntriesAsDiff(index.map, "add")
      ));
      diffsMap.set(name, indexDiffResult);
    }
    for (const [name, basisIndex] of basisIndexes) {
      if (!this.indexes.has(name) && diffConfig.shouldComputeDiffsForIndex(name)) {
        const indexDiffResult = await allEntriesAsDiff(basisIndex.map, "del");
        diffsMap.set(name, indexDiffResult);
      }
    }
    return diffsMap;
  }
  close() {
    this.#dagWrite.release();
  }
};
async function newWriteLocal(basisHash, mutatorName, mutatorArgsJSON, originalHash, dagWrite, timestamp, clientID, formatVersion) {
  const basis = await commitFromHash(basisHash, dagWrite);
  const bTreeWrite = new BTreeWrite(dagWrite, formatVersion, basis.valueHash);
  const mutationID = await basis.getNextMutationID(clientID, dagWrite);
  const indexes = readIndexesForWrite(basis, dagWrite, formatVersion);
  assert(formatVersion >= DD31);
  return new Write(
    dagWrite,
    bTreeWrite,
    basis,
    {
      type: LocalDD31,
      basisHash,
      baseSnapshotHash: await baseSnapshotHashFromHash(basisHash, dagWrite),
      mutatorName,
      mutatorArgsJSON,
      mutationID,
      originalHash,
      timestamp,
      clientID
    },
    indexes,
    clientID,
    formatVersion
  );
}
async function newWriteSnapshotDD31(basisHash, lastMutationIDs, cookieJSON, dagWrite, clientID, formatVersion) {
  const basis = await commitFromHash(basisHash, dagWrite);
  const bTreeWrite = new BTreeWrite(dagWrite, formatVersion, basis.valueHash);
  return new Write(
    dagWrite,
    bTreeWrite,
    basis,
    { basisHash, type: SnapshotDD31, lastMutationIDs, cookieJSON },
    readIndexesForWrite(basis, dagWrite, formatVersion),
    clientID,
    formatVersion
  );
}
async function updateIndexes(lc, indexes, key, oldValGetter, newVal) {
  const ps = [];
  for (const idx of indexes.values()) {
    const { keyPrefix } = idx.meta.definition;
    if (!keyPrefix || key.startsWith(keyPrefix)) {
      const oldVal = await oldValGetter();
      if (oldVal !== void 0) {
        ps.push(
          indexValue(
            lc,
            idx.map,
            Remove,
            key,
            oldVal,
            idx.meta.definition.jsonPointer,
            idx.meta.definition.allowEmpty ?? false
          )
        );
      }
      if (newVal !== void 0) {
        ps.push(
          indexValue(
            lc,
            idx.map,
            Add,
            key,
            newVal,
            idx.meta.definition.jsonPointer,
            idx.meta.definition.allowEmpty ?? false
          )
        );
      }
    }
  }
  await Promise.all(ps);
}
function readIndexesForWrite(commit, dagWrite, formatVersion) {
  const m = /* @__PURE__ */ new Map();
  for (const index of commit.indexes) {
    m.set(
      index.definition.name,
      new IndexWrite(
        index,
        new BTreeWrite(dagWrite, formatVersion, index.valueHash)
      )
    );
  }
  return m;
}
async function createIndexBTree(lc, dagWrite, valueMap, prefix, jsonPointer, allowEmpty, formatVersion) {
  const indexMap = new BTreeWrite(dagWrite, formatVersion);
  for await (const entry of valueMap.scan(prefix)) {
    const key = entry[0];
    if (!key.startsWith(prefix)) {
      break;
    }
    await indexValue(
      lc,
      indexMap,
      Add,
      key,
      entry[1],
      jsonPointer,
      allowEmpty
    );
  }
  return indexMap;
}

// ../replicache/src/sync/ids.ts
var clientGroupIDSchema = valita_exports.string();
var clientIDSchema = valita_exports.string();

// ../replicache/src/persist/make-client-id.ts
function makeClientID() {
  const length = 18;
  const high = randomUint64();
  const low = randomUint64();
  const combined = high << 64n | low;
  return combined.toString(32).slice(-length).padStart(length, "0");
}

// ../replicache/src/persist/clients.ts
var clientV5Schema = readonlyObject({
  heartbeatTimestampMs: valita_exports.number(),
  headHash: hashSchema,
  /**
   * The hash of a commit we are in the middle of refreshing into this client's
   * memdag.
   */
  tempRefreshHash: hashSchema.nullable(),
  /**
   * ID of this client's perdag client group. This needs to be sent in pull
   * request (to enable syncing all last mutation ids in the client group).
   */
  clientGroupID: clientGroupIDSchema
});
var clientV6Schema = readonlyObject({
  heartbeatTimestampMs: valita_exports.number(),
  /**
   * A set of hashes, which contains:
   * 1. The hash of the last commit this client refreshed from its client group
   *    (this is the commit it bootstrapped from until it completes its first
   *    refresh).
   * 2. One or more hashes that were added to retain chunks of a commit while it
   *    was being refreshed into this client's memdag. (This can be one or more
   *    because refresh's cleanup step is a separate transaction and can fail).
   * Upon refresh completing and successfully running its clean up step, this
   * set will contain a single hash: the hash of the last commit this client
   * refreshed.
   */
  refreshHashes: readonlyArray(hashSchema),
  /**
   * The hash of the last snapshot commit persisted by this client to this
   * client's client group, or null if has never persisted a snapshot.
   */
  persistHash: hashSchema.nullable(),
  /**
   * ID of this client's perdag client group. This needs to be sent in pull
   * request (to enable syncing all last mutation ids in the client group).
   */
  clientGroupID: clientGroupIDSchema
});
function isClientV6(client) {
  return client.refreshHashes !== void 0;
}
var CLIENTS_HEAD_NAME = "clients";
var clientSchema = valita_exports.union(clientV5Schema, clientV6Schema);
function assertClient(value) {
  assert2(value, clientSchema);
}
function assertClientV6(value) {
  assert2(value, clientV6Schema);
}
function chunkDataToClientMap(chunkData) {
  assertObject(chunkData);
  const clients = /* @__PURE__ */ new Map();
  for (const key in chunkData) {
    if (hasOwn(chunkData, key)) {
      const value = chunkData[key];
      if (value !== void 0) {
        assertClient(value);
        clients.set(key, value);
      }
    }
  }
  return clients;
}
function clientMapToChunkData(clients, dagWrite) {
  for (const client of clients.values()) {
    if (isClientV6(client)) {
      client.refreshHashes.forEach(dagWrite.assertValidHash);
      if (client.persistHash) {
        dagWrite.assertValidHash(client.persistHash);
      }
    } else {
      dagWrite.assertValidHash(client.headHash);
      if (client.tempRefreshHash) {
        dagWrite.assertValidHash(client.tempRefreshHash);
      }
    }
  }
  return deepFreeze(Object.fromEntries(clients));
}
async function getClients(dagRead) {
  const hash2 = await dagRead.getHead(CLIENTS_HEAD_NAME);
  return getClientsAtHash(hash2, dagRead);
}
async function getClientsAtHash(hash2, dagRead) {
  if (!hash2) {
    return /* @__PURE__ */ new Map();
  }
  const chunk = await dagRead.getChunk(hash2);
  return chunkDataToClientMap(chunk?.data);
}
var ClientStateNotFoundError = class extends Error {
  name = "ClientStateNotFoundError";
  id;
  constructor(id) {
    super(`Client state not found, id: ${id}`);
    this.id = id;
  }
};
async function assertHasClientState(id, dagRead) {
  if (!await hasClientState(id, dagRead)) {
    throw new ClientStateNotFoundError(id);
  }
}
async function hasClientState(id, dagRead) {
  return !!await getClient(id, dagRead);
}
async function getClient(id, dagRead) {
  const clients = await getClients(dagRead);
  return clients.get(id);
}
async function mustGetClient(id, dagRead) {
  const client = await getClient(id, dagRead);
  if (!client) {
    throw new ClientStateNotFoundError(id);
  }
  return client;
}
function initClientV6(newClientID, lc, perdag, mutatorNames, indexes, formatVersion, enableClientGroupForking) {
  return withWrite(perdag, async (dagWrite) => {
    async function setClientsAndClientGroupAndCommit(basisHash, cookieJSON, valueHash2, indexRecords2) {
      const newSnapshotData = newSnapshotCommitDataDD31(
        basisHash,
        {},
        cookieJSON,
        valueHash2,
        indexRecords2
      );
      const chunk = dagWrite.createChunk(
        newSnapshotData,
        getRefs(newSnapshotData)
      );
      const newClientGroupID = makeClientID();
      const newClient = {
        heartbeatTimestampMs: Date.now(),
        refreshHashes: [chunk.hash],
        persistHash: null,
        clientGroupID: newClientGroupID
      };
      const newClients = new Map(clients).set(newClientID, newClient);
      const clientGroup = {
        headHash: chunk.hash,
        mutatorNames,
        indexes,
        mutationIDs: {},
        lastServerAckdMutationIDs: {},
        disabled: false
      };
      await Promise.all([
        dagWrite.putChunk(chunk),
        setClients(newClients, dagWrite),
        setClientGroup(newClientGroupID, clientGroup, dagWrite)
      ]);
      return [newClient, chunk.hash, newClients, true];
    }
    const clients = await getClients(dagWrite);
    const res = await findMatchingClient(dagWrite, mutatorNames, indexes);
    if (res.type === FIND_MATCHING_CLIENT_TYPE_HEAD) {
      const { clientGroupID, headHash } = res;
      const newClient = {
        clientGroupID,
        refreshHashes: [headHash],
        heartbeatTimestampMs: Date.now(),
        persistHash: null
      };
      const newClients = new Map(clients).set(newClientID, newClient);
      await setClients(newClients, dagWrite);
      return [newClient, headHash, newClients, false];
    }
    if (!enableClientGroupForking || res.type === FIND_MATCHING_CLIENT_TYPE_NEW) {
      const emptyBTreeChunk = dagWrite.createChunk(emptyDataNode, []);
      await dagWrite.putChunk(emptyBTreeChunk);
      const indexRecords2 = [];
      for (const [name, indexDefinition] of Object.entries(indexes)) {
        const chunkIndexDefinition = toChunkIndexDefinition(
          name,
          indexDefinition
        );
        indexRecords2.push({
          definition: chunkIndexDefinition,
          valueHash: emptyBTreeChunk.hash
        });
      }
      return setClientsAndClientGroupAndCommit(
        null,
        null,
        emptyBTreeChunk.hash,
        indexRecords2
      );
    }
    assert(res.type === FIND_MATCHING_CLIENT_TYPE_FORK);
    const { snapshot } = res;
    const indexRecords = [];
    const { valueHash, indexes: oldIndexes } = snapshot;
    const map = new BTreeRead(dagWrite, formatVersion, valueHash);
    for (const [name, indexDefinition] of Object.entries(indexes)) {
      const { prefix = "", jsonPointer, allowEmpty = false } = indexDefinition;
      const chunkIndexDefinition = {
        name,
        keyPrefix: prefix,
        jsonPointer,
        allowEmpty
      };
      const oldIndex = findMatchingOldIndex(oldIndexes, chunkIndexDefinition);
      if (oldIndex) {
        indexRecords.push({
          definition: chunkIndexDefinition,
          valueHash: oldIndex.valueHash
        });
      } else {
        const indexBTree = await createIndexBTree(
          lc,
          dagWrite,
          map,
          prefix,
          jsonPointer,
          allowEmpty,
          formatVersion
        );
        indexRecords.push({
          definition: chunkIndexDefinition,
          valueHash: await indexBTree.flush()
        });
      }
    }
    return setClientsAndClientGroupAndCommit(
      snapshot.meta.basisHash,
      snapshot.meta.cookieJSON,
      snapshot.valueHash,
      indexRecords
    );
  });
}
function findMatchingOldIndex(oldIndexes, chunkIndexDefinition) {
  return oldIndexes.find(
    (index) => chunkIndexDefinitionEqualIgnoreName(index.definition, chunkIndexDefinition)
  );
}
var FIND_MATCHING_CLIENT_TYPE_NEW = 0;
var FIND_MATCHING_CLIENT_TYPE_FORK = 1;
var FIND_MATCHING_CLIENT_TYPE_HEAD = 2;
async function findMatchingClient(dagRead, mutatorNames, indexes) {
  let newestCookie;
  let bestSnapshot;
  const mutatorNamesSet = new Set(mutatorNames);
  const clientGroups = await getClientGroups(dagRead);
  for (const [clientGroupID, clientGroup] of clientGroups) {
    if (!clientGroup.disabled && mutatorNamesEqual(mutatorNamesSet, clientGroup.mutatorNames) && indexDefinitionsEqual(indexes, clientGroup.indexes)) {
      return {
        type: FIND_MATCHING_CLIENT_TYPE_HEAD,
        clientGroupID,
        headHash: clientGroup.headHash
      };
    }
    const clientGroupSnapshotCommit = await baseSnapshotFromHash(
      clientGroup.headHash,
      dagRead
    );
    assertSnapshotCommitDD31(clientGroupSnapshotCommit);
    const { cookieJSON } = clientGroupSnapshotCommit.meta;
    if (newestCookie === void 0 || compareCookies(cookieJSON, newestCookie) > 0) {
      newestCookie = cookieJSON;
      bestSnapshot = clientGroupSnapshotCommit;
    }
  }
  if (bestSnapshot) {
    return {
      type: FIND_MATCHING_CLIENT_TYPE_FORK,
      snapshot: bestSnapshot
    };
  }
  return { type: FIND_MATCHING_CLIENT_TYPE_NEW };
}
function getRefsForClients(clients) {
  const refs = /* @__PURE__ */ new Set();
  for (const client of clients.values()) {
    if (isClientV6(client)) {
      for (const hash2 of client.refreshHashes) {
        refs.add(hash2);
      }
      if (client.persistHash) {
        refs.add(client.persistHash);
      }
    } else {
      refs.add(client.headHash);
      if (client.tempRefreshHash) {
        refs.add(client.tempRefreshHash);
      }
    }
  }
  return toRefs(refs);
}
async function getClientGroupForClient(clientID, read) {
  const clientGroupID = await getClientGroupIDForClient(clientID, read);
  if (!clientGroupID) {
    return void 0;
  }
  return getClientGroup(clientGroupID, read);
}
async function getClientGroupIDForClient(clientID, read) {
  const client = await getClient(clientID, read);
  return client?.clientGroupID;
}
async function setClient(clientID, client, dagWrite) {
  const clients = await getClients(dagWrite);
  const newClients = new Map(clients).set(clientID, client);
  return setClients(newClients, dagWrite);
}
async function setClients(clients, dagWrite) {
  const chunkData = clientMapToChunkData(clients, dagWrite);
  const chunk = dagWrite.createChunk(chunkData, getRefsForClients(clients));
  await dagWrite.putChunk(chunk);
  await dagWrite.setHead(CLIENTS_HEAD_NAME, chunk.hash);
  return chunk.hash;
}

// ../zero-protocol/src/ast.ts
import { compareUTF8 as compareUTF82 } from "compare-utf8";

// ../shared/src/arrays.ts
function defined(arr) {
  let i = arr.findIndex((x) => x === void 0);
  if (i < 0) {
    return arr;
  }
  const defined2 = arr.slice(0, i);
  for (i++; i < arr.length; i++) {
    const x = arr[i];
    if (x !== void 0) {
      defined2.push(x);
    }
  }
  return defined2;
}
function areEqual(arr1, arr2) {
  return arr1.length === arr2.length && arr1.every((e, i) => e === arr2[i]);
}

// ../shared/src/json-schema.ts
import * as valita from "@badrap/valita";
var path = [];
var jsonSchema = valita_exports.unknown().chain((v2) => {
  if (isProd) {
    return valita.ok(v2);
  }
  const rv = isJSONValue(v2, path) ? valita.ok(v2) : valita.err({
    message: `Not a JSON value`,
    path: path.slice()
  });
  path.length = 0;
  return rv;
});
var jsonObjectSchema = valita_exports.unknown().chain((v2) => {
  if (isProd) {
    return valita.ok(v2);
  }
  const rv = isJSONObject(v2, path) ? valita.ok(v2) : valita.err({
    message: `Not a JSON object`,
    path: path.slice()
  });
  path.length = 0;
  return rv;
});

// ../zero-protocol/src/data.ts
var valueSchema = valita_exports.union(jsonSchema, valita_exports.undefined());
var rowSchema = readonlyRecord(valueSchema);

// ../zero-protocol/src/ast.ts
var selectorSchema = valita_exports.string();
var toStaticParam = Symbol();
var orderingElementSchema = readonly(
  valita_exports.tuple([selectorSchema, valita_exports.union(valita_exports.literal("asc"), valita_exports.literal("desc"))])
);
var orderingSchema = readonlyArray(orderingElementSchema);
var primitiveSchema = valita_exports.union(
  valita_exports.string(),
  valita_exports.number(),
  valita_exports.boolean(),
  valita_exports.null()
);
var equalityOpsSchema = valita_exports.union(
  valita_exports.literal("="),
  valita_exports.literal("!="),
  valita_exports.literal("IS"),
  valita_exports.literal("IS NOT")
);
var orderOpsSchema = valita_exports.union(
  valita_exports.literal("<"),
  valita_exports.literal(">"),
  valita_exports.literal("<="),
  valita_exports.literal(">=")
);
var likeOpsSchema = valita_exports.union(
  valita_exports.literal("LIKE"),
  valita_exports.literal("NOT LIKE"),
  valita_exports.literal("ILIKE"),
  valita_exports.literal("NOT ILIKE")
);
var inOpsSchema = valita_exports.union(valita_exports.literal("IN"), valita_exports.literal("NOT IN"));
var simpleOperatorSchema = valita_exports.union(
  equalityOpsSchema,
  orderOpsSchema,
  likeOpsSchema,
  inOpsSchema
);
var literalReferenceSchema = readonlyObject({
  type: valita_exports.literal("literal"),
  value: valita_exports.union(
    valita_exports.string(),
    valita_exports.number(),
    valita_exports.boolean(),
    valita_exports.null(),
    readonlyArray(valita_exports.union(valita_exports.string(), valita_exports.number(), valita_exports.boolean()))
  )
});
var columnReferenceSchema = readonlyObject({
  type: valita_exports.literal("column"),
  name: valita_exports.string()
});
var parameterReferenceSchema = readonlyObject({
  type: valita_exports.literal("static"),
  // The "namespace" of the injected parameter.
  // Write authorization will send the value of a row
  // prior to the mutation being run (preMutationRow).
  // Read and write authorization will both send the
  // current authentication data (authData).
  anchor: valita_exports.union(valita_exports.literal("authData"), valita_exports.literal("preMutationRow")),
  field: valita_exports.union(valita_exports.string(), valita_exports.array(valita_exports.string()))
});
var conditionValueSchema = valita_exports.union(
  literalReferenceSchema,
  columnReferenceSchema,
  parameterReferenceSchema
);
var simpleConditionSchema = readonlyObject({
  type: valita_exports.literal("simple"),
  op: simpleOperatorSchema,
  left: conditionValueSchema,
  right: valita_exports.union(parameterReferenceSchema, literalReferenceSchema)
});
var correlatedSubqueryConditionOperatorSchema = valita_exports.union(valita_exports.literal("EXISTS"), valita_exports.literal("NOT EXISTS"));
var correlatedSubqueryConditionSchema = readonlyObject({
  type: valita_exports.literal("correlatedSubquery"),
  related: valita_exports.lazy(() => correlatedSubquerySchema),
  op: correlatedSubqueryConditionOperatorSchema
});
var conditionSchema = valita_exports.union(
  simpleConditionSchema,
  valita_exports.lazy(() => conjunctionSchema),
  valita_exports.lazy(() => disjunctionSchema),
  correlatedSubqueryConditionSchema
);
var conjunctionSchema = readonlyObject({
  type: valita_exports.literal("and"),
  conditions: readonlyArray(conditionSchema)
});
var disjunctionSchema = readonlyObject({
  type: valita_exports.literal("or"),
  conditions: readonlyArray(conditionSchema)
});
function mustCompoundKey(field) {
  assert(Array.isArray(field) && field.length >= 1);
  return field;
}
var compoundKeySchema = readonly(
  valita_exports.tuple([valita_exports.string()]).concat(valita_exports.array(valita_exports.string()))
);
var correlationSchema = readonlyObject({
  parentField: compoundKeySchema,
  childField: compoundKeySchema
});
var correlatedSubquerySchemaOmitSubquery = readonlyObject({
  correlation: correlationSchema,
  hidden: valita_exports.boolean().optional(),
  system: valita_exports.union(valita_exports.literal("permissions"), valita_exports.literal("client")).optional()
});
var correlatedSubquerySchema = correlatedSubquerySchemaOmitSubquery.extend({
  subquery: valita_exports.lazy(() => astSchema)
});
var astSchema = readonlyObject({
  schema: valita_exports.string().optional(),
  table: valita_exports.string(),
  alias: valita_exports.string().optional(),
  where: conditionSchema.optional(),
  related: readonlyArray(correlatedSubquerySchema).optional(),
  limit: valita_exports.number().optional(),
  orderBy: orderingSchema.optional(),
  start: valita_exports.object({
    row: rowSchema,
    exclusive: valita_exports.boolean()
  }).optional()
});
function transformAST(ast, transform) {
  const { tableName, columnName } = transform;
  const colName = (c) => columnName(ast.table, c);
  const key = (table, k) => {
    const serverKey = k.map((col) => columnName(table, col));
    return mustCompoundKey(serverKey);
  };
  const where = ast.where ? transform.where(ast.where) : void 0;
  const transformed = {
    schema: ast.schema,
    table: tableName(ast.table),
    alias: ast.alias,
    where: where ? transformWhere(where, ast.table, transform) : void 0,
    related: ast.related ? transform.related(
      ast.related.map(
        (r) => ({
          correlation: {
            parentField: key(ast.table, r.correlation.parentField),
            childField: key(r.subquery.table, r.correlation.childField)
          },
          hidden: r.hidden,
          subquery: transformAST(r.subquery, transform),
          system: r.system
        })
      )
    ) : void 0,
    start: ast.start ? {
      ...ast.start,
      row: Object.fromEntries(
        Object.entries(ast.start.row).map(([col, val]) => [
          colName(col),
          val
        ])
      )
    } : void 0,
    limit: ast.limit,
    orderBy: ast.orderBy?.map(([col, dir]) => [colName(col), dir])
  };
  return transformed;
}
function transformWhere(where, table, transform) {
  const { columnName } = transform;
  const condValue = (c) => c.type !== "column" ? c : { ...c, name: columnName(table, c.name) };
  const key = (table2, k) => {
    const serverKey = k.map((col) => columnName(table2, col));
    return mustCompoundKey(serverKey);
  };
  if (where.type === "simple") {
    return { ...where, left: condValue(where.left) };
  } else if (where.type === "correlatedSubquery") {
    const { correlation, subquery } = where.related;
    return {
      ...where,
      related: {
        ...where.related,
        correlation: {
          parentField: key(table, correlation.parentField),
          childField: key(subquery.table, correlation.childField)
        },
        subquery: transformAST(subquery, transform)
      }
    };
  }
  return {
    type: where.type,
    conditions: transform.conditions(
      where.conditions.map((c) => transformWhere(c, table, transform))
    )
  };
}
var normalizeCache = /* @__PURE__ */ new WeakMap();
var NORMALIZE_TRANSFORM = {
  tableName: (t) => t,
  columnName: (_, c) => c,
  related: sortedRelated,
  where: flattened,
  conditions: (c) => c.sort(cmpCondition)
};
function normalizeAST(ast) {
  let normalized = normalizeCache.get(ast);
  if (!normalized) {
    normalized = transformAST(ast, NORMALIZE_TRANSFORM);
    normalizeCache.set(ast, normalized);
  }
  return normalized;
}
function mapAST(ast, mapper) {
  return transformAST(ast, {
    tableName: (table) => mapper.tableName(table),
    columnName: (table, col) => mapper.columnName(table, col),
    related: (r) => r,
    where: (w) => w,
    conditions: (c) => c
  });
}
function mapCondition(cond, table, mapper) {
  return transformWhere(cond, table, {
    tableName: (table2) => mapper.tableName(table2),
    columnName: (table2, col) => mapper.columnName(table2, col),
    related: (r) => r,
    where: (w) => w,
    conditions: (c) => c
  });
}
function sortedRelated(related) {
  return related.sort(cmpRelated);
}
function cmpCondition(a, b) {
  if (a.type === "simple") {
    if (b.type !== "simple") {
      return -1;
    }
    return compareValuePosition(a.left, b.left) || compareUTF8MaybeNull(a.op, b.op) || compareValuePosition(a.right, b.right);
  }
  if (b.type === "simple") {
    return 1;
  }
  if (a.type === "correlatedSubquery") {
    if (b.type !== "correlatedSubquery") {
      return -1;
    }
    return cmpRelated(a.related, b.related) || compareUTF8MaybeNull(a.op, b.op);
  }
  if (b.type === "correlatedSubquery") {
    return -1;
  }
  const val = compareUTF8MaybeNull(a.type, b.type);
  if (val !== 0) {
    return val;
  }
  for (let l = 0, r = 0; l < a.conditions.length && r < b.conditions.length; l++, r++) {
    const val2 = cmpCondition(a.conditions[l], b.conditions[r]);
    if (val2 !== 0) {
      return val2;
    }
  }
  return a.conditions.length - b.conditions.length;
}
function compareValuePosition(a, b) {
  if (a.type !== b.type) {
    return compareUTF82(a.type, b.type);
  }
  switch (a.type) {
    case "literal":
      assert(b.type === "literal");
      return compareUTF82(String(a.value), String(b.value));
    case "column":
      assert(b.type === "column");
      return compareUTF82(a.name, b.name);
    case "static":
      throw new Error(
        "Static parameters should be resolved before normalization"
      );
  }
}
function cmpRelated(a, b) {
  return compareUTF82(must(a.subquery.alias), must(b.subquery.alias));
}
function flattened(cond) {
  if (cond.type === "simple" || cond.type === "correlatedSubquery") {
    return cond;
  }
  const conditions = defined(
    cond.conditions.flatMap(
      (c) => c.type === cond.type ? c.conditions.map((c2) => flattened(c2)) : flattened(c)
    )
  );
  switch (conditions.length) {
    case 0:
      return void 0;
    case 1:
      return conditions[0];
    default:
      return {
        type: cond.type,
        conditions
      };
  }
}
function compareUTF8MaybeNull(a, b) {
  if (a !== null && b !== null) {
    return compareUTF82(a, b);
  }
  if (b !== null) {
    return -1;
  }
  if (a !== null) {
    return 1;
  }
  return 0;
}

// ../zero-protocol/src/inspect-down.ts
var inspectQueryRowSchema = valita_exports.object({
  clientID: valita_exports.string(),
  queryID: valita_exports.string(),
  ast: astSchema,
  got: valita_exports.boolean(),
  deleted: valita_exports.boolean(),
  ttl: valita_exports.number(),
  inactivatedAt: valita_exports.number().nullable(),
  rowCount: valita_exports.number()
});
var inspectQueriesDownSchema = valita_exports.object({
  op: valita_exports.literal("queries"),
  id: valita_exports.string(),
  value: valita_exports.array(inspectQueryRowSchema)
});
var inspectDownBodySchema = valita_exports.union(inspectQueriesDownSchema);
var inspectDownMessageSchema = valita_exports.tuple([
  valita_exports.literal("inspect"),
  inspectDownBodySchema
]);

// ../shared/src/random-values.ts
function getNonCryptoRandomValues(array5) {
  if (array5 === null) {
    throw new TypeError("array cannot be null");
  }
  for (let i = 0; i < array5.length; i++) {
    array5[i] = Math.floor(Math.random() * 256);
  }
  return array5;
}

// ../zero-client/src/util/nanoid.ts
function nanoid(size = 21) {
  const randomBytes = getNonCryptoRandomValues(new Uint8Array(size));
  return randomBytes.reduce((id, byte) => {
    byte &= 63;
    if (byte < 36) {
      id += byte.toString(36);
    } else if (byte < 62) {
      id += (byte - 26).toString(36).toUpperCase();
    } else if (byte > 62) {
      id += "-";
    } else {
      id += "_";
    }
    return id;
  }, "");
}

// ../shared/src/hash.ts
import { xxHash32 } from "js-xxhash";
var h64 = (s) => hash(s, 2);
var h128 = (s) => hash(s, 4);
function hash(str, words) {
  let hash2 = 0n;
  for (let i = 0; i < words; i++) {
    hash2 = (hash2 << 32n) + BigInt(xxHash32(str, i));
  }
  return hash2;
}

// ../zero-protocol/src/primary-key.ts
var primaryKeySchema = readonly(
  valita_exports.tuple([valita_exports.string()]).concat(valita_exports.array(valita_exports.string()))
);
var primaryKeyValueSchema = valita_exports.union(
  valita_exports.string(),
  valita_exports.number(),
  valita_exports.boolean()
);
var primaryKeyValueRecordSchema = readonlyRecord(
  primaryKeyValueSchema
);

// ../zero-client/src/client/keys.ts
var DESIRED_QUERIES_KEY_PREFIX = "d/";
var GOT_QUERIES_KEY_PREFIX = "g/";
var ENTITIES_KEY_PREFIX = "e/";
function toDesiredQueriesKey(clientID, hash2) {
  return DESIRED_QUERIES_KEY_PREFIX + clientID + "/" + hash2;
}
function desiredQueriesPrefixForClient(clientID) {
  return DESIRED_QUERIES_KEY_PREFIX + clientID + "/";
}
function toGotQueriesKey(hash2) {
  return GOT_QUERIES_KEY_PREFIX + hash2;
}
function toPrimaryKeyString(tableName, primaryKey, value) {
  if (primaryKey.length === 1) {
    return ENTITIES_KEY_PREFIX + tableName + "/" + parse(value[primaryKey[0]], primaryKeyValueSchema);
  }
  const values = primaryKey.map((k) => parse(value[k], primaryKeyValueSchema));
  const str = JSON.stringify(values);
  const idSegment = h128(str);
  return ENTITIES_KEY_PREFIX + tableName + "/" + idSegment;
}
function sourceNameFromKey(key) {
  const slash = key.indexOf("/", ENTITIES_KEY_PREFIX.length);
  return key.slice(ENTITIES_KEY_PREFIX.length, slash);
}

// ../zero-schema/src/table-schema.ts
function isOneHop(r) {
  return r.length === 1;
}
function isTwoHop(r) {
  return r.length === 2;
}

// ../zql/src/query/query-impl.ts
import { resolver } from "@rocicorp/resolver";

// ../zero-protocol/src/ast-hash.ts
var hashCache = /* @__PURE__ */ new WeakMap();
function hashOfAST(ast) {
  const normalized = normalizeAST(ast);
  const cached = hashCache.get(normalized);
  if (cached) {
    return cached;
  }
  const hash2 = h64(JSON.stringify(normalized)).toString(36);
  hashCache.set(normalized, hash2);
  return hash2;
}

// ../zql/src/ivm/operator.ts
var throwOutput = {
  push(_change) {
    throw new Error("Output not set");
  }
};

// ../zql/src/ivm/stream.ts
function* take(stream, limit) {
  if (limit < 1) {
    return;
  }
  let count = 0;
  for (const v2 of stream) {
    yield v2;
    if (++count === limit) {
      break;
    }
  }
}
function first(stream) {
  const it = stream[Symbol.iterator]();
  const { value } = it.next();
  it.return?.();
  return value;
}

// ../zql/src/ivm/exists.ts
var Exists = class {
  #input;
  #relationshipName;
  #storage;
  #not;
  #parentJoinKey;
  #noSizeReuse;
  #output = throwOutput;
  /**
   * This instance variable is `true` when this operator is processing a `push`,
   * and is used to disable reuse of cached sizes across rows with the
   * same parent join key value.
   * This is necessary because during a push relationships can be inconsistent
   * due to push communicating changes (which may change multiple Nodes) one
   * Node at a time.
   */
  #inPush = false;
  constructor(input, storage, relationshipName, parentJoinKey, type) {
    this.#input = input;
    this.#relationshipName = relationshipName;
    this.#input.setOutput(this);
    this.#storage = storage;
    assert(
      this.#input.getSchema().relationships[relationshipName],
      `Input schema missing ${relationshipName}`
    );
    this.#not = type === "NOT EXISTS";
    this.#parentJoinKey = parentJoinKey;
    this.#noSizeReuse = areEqual(
      parentJoinKey,
      this.#input.getSchema().primaryKey
    );
  }
  setOutput(output) {
    this.#output = output;
  }
  destroy() {
    this.#input.destroy();
  }
  getSchema() {
    return this.#input.getSchema();
  }
  *fetch(req) {
    for (const node of this.#input.fetch(req)) {
      if (this.#filter(node)) {
        yield node;
      }
    }
  }
  *cleanup(req) {
    for (const node of this.#input.cleanup(req)) {
      if (this.#filter(node)) {
        yield node;
      } else {
        drainStreams(node);
      }
      this.#delSize(node);
    }
  }
  push(change) {
    this.#inPush = true;
    try {
      switch (change.type) {
        // add, remove and edit cannot change the size of the
        // this.#relationshipName relationship, so simply #pushWithFilter
        case "add":
        case "edit": {
          this.#pushWithFilter(change);
          return;
        }
        case "remove": {
          const size = this.#getSize(change.node);
          if (size === void 0) {
            return;
          }
          this.#pushWithFilter(change, size);
          this.#delSize(change.node);
          return;
        }
        case "child":
          if (change.child.relationshipName !== this.#relationshipName || change.child.change.type === "edit" || change.child.change.type === "child") {
            this.#pushWithFilter(change);
            return;
          }
          switch (change.child.change.type) {
            case "add": {
              let size = this.#getSize(change.node);
              if (size !== void 0) {
                size++;
                this.#setSize(change.node, size);
              } else {
                size = this.#fetchSize(change.node);
              }
              if (size === 1) {
                if (this.#not) {
                  this.#output.push({
                    type: "remove",
                    node: {
                      row: change.node.row,
                      relationships: {
                        ...change.node.relationships,
                        [this.#relationshipName]: () => []
                      }
                    }
                  });
                } else {
                  this.#output.push({
                    type: "add",
                    node: change.node
                  });
                }
              } else {
                this.#pushWithFilter(change, size);
              }
              return;
            }
            case "remove": {
              let size = this.#getSize(change.node);
              if (size !== void 0) {
                assert(size > 0);
                size--;
                this.#setSize(change.node, size);
              } else {
                size = this.#fetchSize(change.node);
              }
              if (size === 0) {
                if (this.#not) {
                  this.#output.push({
                    type: "add",
                    node: change.node
                  });
                } else {
                  this.#output.push({
                    type: "remove",
                    node: {
                      row: change.node.row,
                      relationships: {
                        ...change.node.relationships,
                        [this.#relationshipName]: () => [
                          change.child.change.node
                        ]
                      }
                    }
                  });
                }
              } else {
                this.#pushWithFilter(change, size);
              }
              return;
            }
          }
          return;
        default:
          unreachable(change);
      }
    } finally {
      this.#inPush = false;
    }
  }
  /**
   * Returns whether or not the node's this.#relationshipName
   * relationship passes the exist/not exists filter condition.
   * If the optional `size` is passed it is used.
   * Otherwise, if there is a stored size for the row it is used.
   * Otherwise the size is computed by streaming the node's
   * relationship with this.#relationshipName (this computed size is also
   * stored).
   */
  #filter(node, size) {
    const exists = (size ?? this.#getOrFetchSize(node)) > 0;
    return this.#not ? !exists : exists;
  }
  /**
   * Pushes a change if this.#filter is true for its row.
   */
  #pushWithFilter(change, size) {
    if (this.#filter(change.node, size)) {
      this.#output.push(change);
    }
  }
  #getSize(node) {
    return this.#storage.get(this.#makeSizeStorageKey(node));
  }
  #setSize(node, size) {
    this.#storage.set(this.#makeSizeStorageKey(node), size);
  }
  #delSize(node) {
    this.#storage.del(this.#makeSizeStorageKey(node));
  }
  #getOrFetchSize(node) {
    const size = this.#getSize(node);
    if (size !== void 0) {
      return size;
    }
    return this.#fetchSize(node);
  }
  #fetchSize(node) {
    if (!this.#noSizeReuse && !this.#inPush) {
      const cachedSizeEntry = first(
        this.#storage.scan({
          prefix: this.#makeSizeStorageKeyPrefix(node)
        })
      );
      if (cachedSizeEntry !== void 0) {
        this.#setSize(node, cachedSizeEntry[1]);
        return cachedSizeEntry[1];
      }
    }
    const relationship = node.relationships[this.#relationshipName];
    assert(relationship);
    let size = 0;
    for (const _relatedNode of relationship()) {
      size++;
    }
    this.#setSize(node, size);
    return size;
  }
  #makeSizeStorageKeyPrefix(node) {
    return `row/${this.#noSizeReuse ? "" : JSON.stringify(this.#getKeyValues(node, this.#parentJoinKey))}/`;
  }
  #makeSizeStorageKey(node) {
    return `${this.#makeSizeStorageKeyPrefix(node)}${JSON.stringify(
      this.#getKeyValues(node, this.#input.getSchema().primaryKey)
    )}`;
  }
  #getKeyValues(node, def) {
    const values = [];
    for (const key of def) {
      values.push(normalizeUndefined(node.row[key]));
    }
    return values;
  }
};

// ../zql/src/ivm/fan-in.ts
var FanIn = class {
  #inputs;
  #schema;
  #output = throwOutput;
  #accumulatedPushes;
  constructor(fanOut, inputs) {
    this.#inputs = inputs;
    this.#schema = fanOut.getSchema();
    for (const input of inputs) {
      input.setOutput(this);
      assert(this.#schema === input.getSchema(), `Schema mismatch in fan-in`);
    }
    this.#accumulatedPushes = [];
  }
  setOutput(output) {
    this.#output = output;
  }
  destroy() {
    for (const input of this.#inputs) {
      input.destroy();
    }
  }
  getSchema() {
    return this.#schema;
  }
  fetch(req) {
    return this.#fetchOrCleanup((input) => input.fetch(req));
  }
  cleanup(req) {
    return this.#fetchOrCleanup((input) => input.cleanup(req));
  }
  *#fetchOrCleanup(streamProvider) {
    const iterables = this.#inputs.map((input) => streamProvider(input));
    yield* mergeIterables(
      iterables,
      (l, r) => must(this.#schema).compareRows(l.row, r.row),
      true
    );
  }
  push(change) {
    this.#accumulatedPushes.push(change);
  }
  fanOutDonePushingToAllBranches(fanOutChangeType) {
    if (this.#inputs.length === 0) {
      assert(
        this.#accumulatedPushes.length === 0,
        "If there are no inputs then fan-in should not receive any pushes."
      );
      return;
    }
    if (this.#accumulatedPushes.length === 0) {
      return;
    }
    const candidatesToPush = /* @__PURE__ */ new Map();
    for (const change of this.#accumulatedPushes) {
      if (fanOutChangeType === "child" && change.type !== "child") {
        assert(
          candidatesToPush.has(change.type) === false,
          () => `Fan-in:child expected at most one ${change.type} when fan-out is of type child`
        );
      }
      candidatesToPush.set(change.type, change);
    }
    this.#accumulatedPushes = [];
    const types = [...candidatesToPush.keys()];
    switch (fanOutChangeType) {
      case "remove":
        assert(
          types.length === 1 && types[0] === "remove",
          "Fan-in:remove expected all removes"
        );
        this.#output.push(must(candidatesToPush.get("remove")));
        return;
      case "add":
        assert(
          types.length === 1 && types[0] === "add",
          "Fan-in:add expected all adds"
        );
        this.#output.push(must(candidatesToPush.get("add")));
        return;
      case "edit": {
        assert(
          types.every(
            (type) => type === "add" || type === "remove" || type === "edit"
          ),
          "Fan-in:edit expected all adds, removes, or edits"
        );
        const addChange = candidatesToPush.get("add");
        const removeChange = candidatesToPush.get("remove");
        const editChange = candidatesToPush.get("edit");
        if (editChange) {
          this.#output.push(editChange);
          return;
        }
        if (addChange && removeChange) {
          this.#output.push({
            type: "edit",
            node: addChange.node,
            oldNode: removeChange.node
          });
          return;
        }
        this.#output.push(must(addChange ?? removeChange));
        return;
      }
      case "child": {
        assert(
          types.every(
            (type) => type === "add" || // exists can change child to add or remove
            type === "remove" || // exists can change child to add or remove
            type === "child"
            // other operators may preserve the child change
          ),
          "Fan-in:child expected all adds, removes, or children"
        );
        assert(
          types.length <= 2,
          "Fan-in:child expected at most 2 types on a child change from fan-out"
        );
        const childChange = candidatesToPush.get("child");
        if (childChange) {
          this.#output.push(childChange);
          return;
        }
        const addChange = candidatesToPush.get("add");
        const removeChange = candidatesToPush.get("remove");
        assert(
          addChange === void 0 || removeChange === void 0,
          "Fan-in:child expected either add or remove, not both"
        );
        this.#output.push(must(addChange ?? removeChange));
        return;
      }
      default:
        fanOutChangeType;
    }
  }
};

// ../zql/src/ivm/fan-out.ts
var FanOut = class {
  #input;
  #outputs = [];
  #fanIn;
  #destroyCount = 0;
  constructor(input) {
    this.#input = input;
    input.setOutput(this);
  }
  setFanIn(fanIn) {
    this.#fanIn = fanIn;
  }
  setOutput(output) {
    this.#outputs.push(output);
  }
  destroy() {
    if (this.#destroyCount < this.#outputs.length) {
      if (this.#destroyCount === 0) {
        this.#input.destroy();
      }
      ++this.#destroyCount;
    } else {
      throw new Error("FanOut already destroyed once for each output");
    }
  }
  getSchema() {
    return this.#input.getSchema();
  }
  fetch(req) {
    return this.#input.fetch(req);
  }
  cleanup(req) {
    return this.#input.cleanup(req);
  }
  push(change) {
    for (const out of this.#outputs) {
      out.push(change);
    }
    must(
      this.#fanIn,
      "fan-out must have a corresponding fan-in set!"
    ).fanOutDonePushingToAllBranches(change.type);
  }
};

// ../zql/src/ivm/maybe-split-and-push-edit-change.ts
function maybeSplitAndPushEditChange(change, predicate, output) {
  const oldWasPresent = predicate(change.oldNode.row);
  const newIsPresent = predicate(change.node.row);
  if (oldWasPresent && newIsPresent) {
    output.push(change);
  } else if (oldWasPresent && !newIsPresent) {
    output.push({
      type: "remove",
      node: change.oldNode
    });
  } else if (!oldWasPresent && newIsPresent) {
    output.push({
      type: "add",
      node: change.node
    });
  }
}

// ../zql/src/ivm/filter-push.ts
function filterPush(change, output, predicate) {
  if (!predicate) {
    output.push(change);
    return;
  }
  switch (change.type) {
    case "add":
    case "remove":
      if (predicate(change.node.row)) {
        output.push(change);
      }
      break;
    case "child":
      if (predicate(change.node.row)) {
        output.push(change);
      }
      break;
    case "edit":
      maybeSplitAndPushEditChange(change, predicate, output);
      break;
    default:
      unreachable(change);
  }
}

// ../zql/src/ivm/filter.ts
var Filter = class {
  #input;
  #predicate;
  #output = throwOutput;
  constructor(input, predicate) {
    this.#input = input;
    this.#predicate = predicate;
    input.setOutput(this);
  }
  setOutput(output) {
    this.#output = output;
  }
  destroy() {
    this.#input.destroy();
  }
  getSchema() {
    return this.#input.getSchema();
  }
  *fetch(req) {
    for (const node of this.#input.fetch(req)) {
      if (this.#predicate(node.row)) {
        yield node;
      }
    }
  }
  *cleanup(req) {
    for (const node of this.#input.cleanup(req)) {
      if (this.#predicate(node.row)) {
        yield node;
      } else {
        drainStreams(node);
      }
    }
  }
  push(change) {
    filterPush(change, this.#output, this.#predicate);
  }
};

// ../zql/src/ivm/join.ts
var Join = class {
  #parent;
  #child;
  #storage;
  #parentKey;
  #childKey;
  #relationshipName;
  #schema;
  #output = throwOutput;
  #inprogressChildChange;
  constructor({
    parent,
    child,
    storage,
    parentKey,
    childKey,
    relationshipName,
    hidden,
    system
  }) {
    assert(parent !== child, "Parent and child must be different operators");
    assert(
      parentKey.length === childKey.length,
      "The parentKey and childKey keys must have same length"
    );
    this.#parent = parent;
    this.#child = child;
    this.#storage = storage;
    this.#parentKey = parentKey;
    this.#childKey = childKey;
    this.#relationshipName = relationshipName;
    const parentSchema = parent.getSchema();
    const childSchema = child.getSchema();
    this.#schema = {
      ...parentSchema,
      relationships: {
        ...parentSchema.relationships,
        [relationshipName]: {
          ...childSchema,
          isHidden: hidden,
          system
        }
      }
    };
    parent.setOutput({
      push: (change) => this.#pushParent(change)
    });
    child.setOutput({
      push: (change) => this.#pushChild(change)
    });
  }
  destroy() {
    this.#parent.destroy();
    this.#child.destroy();
  }
  setOutput(output) {
    this.#output = output;
  }
  getSchema() {
    return this.#schema;
  }
  *fetch(req) {
    for (const parentNode of this.#parent.fetch(req)) {
      yield this.#processParentNode(
        parentNode.row,
        parentNode.relationships,
        "fetch"
      );
    }
  }
  *cleanup(req) {
    for (const parentNode of this.#parent.cleanup(req)) {
      yield this.#processParentNode(
        parentNode.row,
        parentNode.relationships,
        "cleanup"
      );
    }
  }
  #pushParent(change) {
    switch (change.type) {
      case "add":
        this.#output.push({
          type: "add",
          node: this.#processParentNode(
            change.node.row,
            change.node.relationships,
            "fetch"
          )
        });
        break;
      case "remove":
        this.#output.push({
          type: "remove",
          node: this.#processParentNode(
            change.node.row,
            change.node.relationships,
            "cleanup"
          )
        });
        break;
      case "child":
        this.#output.push({
          type: "child",
          node: this.#processParentNode(
            change.node.row,
            change.node.relationships,
            "fetch"
          ),
          child: change.child
        });
        break;
      case "edit": {
        assert(
          rowEqualsForCompoundKey(
            change.oldNode.row,
            change.node.row,
            this.#parentKey
          ),
          `Parent edit must not change relationship.`
        );
        this.#output.push({
          type: "edit",
          oldNode: this.#processParentNode(
            change.oldNode.row,
            change.oldNode.relationships,
            "cleanup"
          ),
          node: this.#processParentNode(
            change.node.row,
            change.node.relationships,
            "fetch"
          )
        });
        break;
      }
      default:
        unreachable(change);
    }
  }
  #pushChild(change) {
    const pushChildChange = (childRow, change2) => {
      this.#inprogressChildChange = {
        change: change2,
        position: void 0
      };
      try {
        const parentNodes = this.#parent.fetch({
          constraint: Object.fromEntries(
            this.#parentKey.map((key, i) => [key, childRow[this.#childKey[i]]])
          )
        });
        for (const parentNode of parentNodes) {
          this.#inprogressChildChange.position = parentNode.row;
          const childChange = {
            type: "child",
            node: this.#processParentNode(
              parentNode.row,
              parentNode.relationships,
              "fetch"
            ),
            child: {
              relationshipName: this.#relationshipName,
              change: change2
            }
          };
          this.#output.push(childChange);
        }
      } finally {
        this.#inprogressChildChange = void 0;
      }
    };
    switch (change.type) {
      case "add":
      case "remove":
        pushChildChange(change.node.row, change);
        break;
      case "child":
        pushChildChange(change.node.row, change);
        break;
      case "edit": {
        const childRow = change.node.row;
        const oldChildRow = change.oldNode.row;
        assert(
          rowEqualsForCompoundKey(oldChildRow, childRow, this.#childKey),
          "Child edit must not change relationship."
        );
        pushChildChange(childRow, change);
        break;
      }
      default:
        unreachable(change);
    }
  }
  *#generateChildStreamWithOverlay(stream, overlay) {
    let applied = false;
    let editOldApplied = false;
    let editNewApplied = false;
    for (const child of stream) {
      let yieldChild = true;
      if (!applied) {
        switch (overlay.type) {
          case "add": {
            if (this.#child.getSchema().compareRows(overlay.node.row, child.row) === 0) {
              applied = true;
              yieldChild = false;
            }
            break;
          }
          case "remove": {
            if (this.#child.getSchema().compareRows(overlay.node.row, child.row) < 0) {
              applied = true;
              yield overlay.node;
            }
            break;
          }
          case "edit": {
            if (this.#child.getSchema().compareRows(overlay.oldNode.row, child.row) < 0) {
              editOldApplied = true;
              if (editNewApplied) {
                applied = true;
              }
              yield overlay.oldNode;
            }
            if (this.#child.getSchema().compareRows(overlay.node.row, child.row) === 0) {
              editNewApplied = true;
              if (editOldApplied) {
                applied = true;
              }
              yieldChild = false;
            }
            break;
          }
          case "child": {
            if (this.#child.getSchema().compareRows(overlay.node.row, child.row) === 0) {
              applied = true;
              yield {
                row: child.row,
                relationships: {
                  ...child.relationships,
                  [overlay.child.relationshipName]: () => this.#generateChildStreamWithOverlay(
                    child.relationships[overlay.child.relationshipName](),
                    overlay.child.change
                  )
                }
              };
              yieldChild = false;
            }
            break;
          }
        }
      }
      if (yieldChild) {
        yield child;
      }
    }
    if (!applied) {
      if (overlay.type === "remove") {
        applied = true;
        yield overlay.node;
      } else if (overlay.type === "edit") {
        assert(editNewApplied);
        editOldApplied = true;
        applied = true;
        yield overlay.oldNode;
      }
    }
    assert(applied);
  }
  #processParentNode(parentNodeRow, parentNodeRelations, mode) {
    let method = mode;
    let storageUpdated = false;
    const childStream = () => {
      if (!storageUpdated) {
        if (mode === "cleanup") {
          this.#storage.del(
            makeStorageKey(
              this.#parentKey,
              this.#parent.getSchema().primaryKey,
              parentNodeRow
            )
          );
          const empty = [
            ...take(
              this.#storage.scan({
                prefix: makeStorageKeyPrefix(parentNodeRow, this.#parentKey)
              }),
              1
            )
          ].length === 0;
          method = empty ? "cleanup" : "fetch";
        }
        storageUpdated = true;
        if (mode === "fetch") {
          this.#storage.set(
            makeStorageKey(
              this.#parentKey,
              this.#parent.getSchema().primaryKey,
              parentNodeRow
            ),
            true
          );
        }
      }
      const stream = this.#child[method]({
        constraint: Object.fromEntries(
          this.#childKey.map((key, i) => [
            key,
            parentNodeRow[this.#parentKey[i]]
          ])
        )
      });
      if (this.#inprogressChildChange && this.#isJoinMatch(
        parentNodeRow,
        this.#inprogressChildChange.change.node.row
      ) && this.#inprogressChildChange.position && this.#schema.compareRows(
        parentNodeRow,
        this.#inprogressChildChange.position
      ) > 0) {
        return this.#generateChildStreamWithOverlay(
          stream,
          this.#inprogressChildChange.change
        );
      }
      return stream;
    };
    return {
      row: parentNodeRow,
      relationships: {
        ...parentNodeRelations,
        [this.#relationshipName]: childStream
      }
    };
  }
  #isJoinMatch(parent, child) {
    for (let i = 0; i < this.#parentKey.length; i++) {
      if (!valuesEqual(parent[this.#parentKey[i]], child[this.#childKey[i]])) {
        return false;
      }
    }
    return true;
  }
};
function makeStorageKeyForValues(values) {
  const json = JSON.stringify(["pKeySet", ...values]);
  return json.substring(1, json.length - 1) + ",";
}
function makeStorageKeyPrefix(row, key) {
  return makeStorageKeyForValues(key.map((k) => row[k]));
}
function makeStorageKey(key, primaryKey, row) {
  const values = key.map((k) => row[k]);
  for (const key2 of primaryKey) {
    values.push(row[key2]);
  }
  return makeStorageKeyForValues(values);
}
function rowEqualsForCompoundKey(a, b, key) {
  for (let i = 0; i < key.length; i++) {
    if (compareValues(a[key[i]], b[key[i]]) !== 0) {
      return false;
    }
  }
  return true;
}

// ../zql/src/ivm/skip.ts
var Skip = class {
  #input;
  #bound;
  #comparator;
  #output = throwOutput;
  constructor(input, bound) {
    this.#input = input;
    this.#bound = bound;
    this.#comparator = input.getSchema().compareRows;
    input.setOutput(this);
  }
  getSchema() {
    return this.#input.getSchema();
  }
  fetch(req) {
    return this.#fetchOrCleanup("fetch", req);
  }
  cleanup(req) {
    return this.#fetchOrCleanup("fetch", req);
  }
  *#fetchOrCleanup(method, req) {
    const start = this.#getStart(req);
    if (start === "empty") {
      return;
    }
    const nodes = this.#input[method]({ ...req, start });
    if (!req.reverse) {
      yield* nodes;
      return;
    }
    for (const node of nodes) {
      if (!this.#shouldBePresent(node.row)) {
        return;
      }
      yield node;
    }
  }
  setOutput(output) {
    this.#output = output;
  }
  destroy() {
    this.#input.destroy();
  }
  #shouldBePresent(row) {
    const cmp2 = this.#comparator(this.#bound.row, row);
    return cmp2 < 0 || cmp2 === 0 && !this.#bound.exclusive;
  }
  push(change) {
    const shouldBePresent = (row) => this.#shouldBePresent(row);
    if (change.type === "edit") {
      maybeSplitAndPushEditChange(change, shouldBePresent, this.#output);
      return;
    }
    change;
    if (shouldBePresent(change.node.row)) {
      this.#output.push(change);
    }
  }
  #getStart(req) {
    const boundStart = {
      row: this.#bound.row,
      basis: this.#bound.exclusive ? "after" : "at"
    };
    if (!req.start) {
      if (req.reverse) {
        return void 0;
      }
      return boundStart;
    }
    const cmp2 = this.#comparator(this.#bound.row, req.start.row);
    if (!req.reverse) {
      if (cmp2 > 0) {
        return boundStart;
      }
      if (cmp2 === 0) {
        if (this.#bound.exclusive || req.start.basis === "after") {
          return {
            row: this.#bound.row,
            basis: "after"
          };
        }
        return boundStart;
      }
      return req.start;
    }
    req.reverse;
    if (cmp2 > 0) {
      return "empty";
    }
    if (cmp2 === 0) {
      if (!this.#bound.exclusive && req.start.basis === "at") {
        return boundStart;
      }
      return "empty";
    }
    return req.start;
  }
};

// ../zql/src/ivm/take.ts
var MAX_BOUND_KEY = "maxBound";
var Take = class {
  #input;
  #storage;
  #limit;
  #partitionKey;
  #partitionKeyComparator;
  // Fetch overlay needed for some split push cases.
  #rowHiddenFromFetch;
  #output = throwOutput;
  constructor(input, storage, limit, partitionKey) {
    assert(limit >= 0);
    assertOrderingIncludesPK(
      input.getSchema().sort,
      input.getSchema().primaryKey
    );
    input.setOutput(this);
    this.#input = input;
    this.#storage = storage;
    this.#limit = limit;
    this.#partitionKey = partitionKey;
    this.#partitionKeyComparator = partitionKey && makePartitionKeyComparator(partitionKey);
  }
  setOutput(output) {
    this.#output = output;
  }
  getSchema() {
    return this.#input.getSchema();
  }
  *fetch(req) {
    if (!this.#partitionKey || req.constraint && constraintMatchesPartitionKey(req.constraint, this.#partitionKey)) {
      const takeStateKey = getTakeStateKey(this.#partitionKey, req.constraint);
      const takeState = this.#storage.get(takeStateKey);
      if (!takeState) {
        yield* this.#initialFetch(req);
        return;
      }
      if (takeState.bound === void 0) {
        return;
      }
      for (const inputNode of this.#input.fetch(req)) {
        if (this.getSchema().compareRows(takeState.bound, inputNode.row) < 0) {
          return;
        }
        if (this.#rowHiddenFromFetch && this.getSchema().compareRows(
          this.#rowHiddenFromFetch,
          inputNode.row
        ) === 0) {
          continue;
        }
        yield inputNode;
      }
      return;
    }
    const maxBound = this.#storage.get(MAX_BOUND_KEY);
    if (maxBound === void 0) {
      return;
    }
    for (const inputNode of this.#input.fetch(req)) {
      if (this.getSchema().compareRows(inputNode.row, maxBound) > 0) {
        return;
      }
      const takeStateKey = getTakeStateKey(this.#partitionKey, inputNode.row);
      const takeState = this.#storage.get(takeStateKey);
      if (takeState?.bound !== void 0 && this.getSchema().compareRows(takeState.bound, inputNode.row) >= 0) {
        yield inputNode;
      }
    }
  }
  *#initialFetch(req) {
    assert(req.start === void 0);
    assert(!req.reverse);
    assert(constraintMatchesPartitionKey(req.constraint, this.#partitionKey));
    if (this.#limit === 0) {
      return;
    }
    const takeStateKey = getTakeStateKey(this.#partitionKey, req.constraint);
    assert(this.#storage.get(takeStateKey) === void 0);
    let size = 0;
    let bound;
    let downstreamEarlyReturn = true;
    let exceptionThrown = false;
    try {
      for (const inputNode of this.#input.fetch(req)) {
        yield inputNode;
        bound = inputNode.row;
        size++;
        if (size === this.#limit) {
          break;
        }
      }
      downstreamEarlyReturn = false;
    } catch (e) {
      exceptionThrown = true;
      throw e;
    } finally {
      if (!exceptionThrown) {
        this.#setTakeState(
          takeStateKey,
          size,
          bound,
          this.#storage.get(MAX_BOUND_KEY)
        );
        assert(
          !downstreamEarlyReturn,
          "Unexpected early return prevented full hydration"
        );
      }
    }
  }
  *cleanup(req) {
    assert(req.start === void 0);
    assert(constraintMatchesPartitionKey(req.constraint, this.#partitionKey));
    const takeStateKey = getTakeStateKey(this.#partitionKey, req.constraint);
    this.#storage.del(takeStateKey);
    let size = 0;
    for (const inputNode of this.#input.cleanup(req)) {
      if (size === this.#limit) {
        return;
      }
      size++;
      yield inputNode;
    }
  }
  #getStateAndConstraint(row) {
    const takeStateKey = getTakeStateKey(this.#partitionKey, row);
    const takeState = this.#storage.get(takeStateKey);
    let maxBound;
    let constraint;
    if (takeState) {
      maxBound = this.#storage.get(MAX_BOUND_KEY);
      constraint = this.#partitionKey && Object.fromEntries(
        this.#partitionKey.map((key) => [key, row[key]])
      );
    }
    return { takeState, takeStateKey, maxBound, constraint };
  }
  push(change) {
    if (change.type === "edit") {
      this.#pushEditChange(change);
      return;
    }
    const { takeState, takeStateKey, maxBound, constraint } = this.#getStateAndConstraint(change.node.row);
    if (!takeState) {
      return;
    }
    const { compareRows } = this.getSchema();
    if (change.type === "add") {
      if (takeState.size < this.#limit) {
        this.#setTakeState(
          takeStateKey,
          takeState.size + 1,
          takeState.bound === void 0 || compareRows(takeState.bound, change.node.row) < 0 ? change.node.row : takeState.bound,
          maxBound
        );
        this.#output.push(change);
        return;
      }
      if (takeState.bound === void 0 || compareRows(change.node.row, takeState.bound) >= 0) {
        return;
      }
      let beforeBoundNode;
      let boundNode;
      if (this.#limit === 1) {
        boundNode = must(
          first(
            this.#input.fetch({
              start: {
                row: takeState.bound,
                basis: "at"
              },
              constraint
            })
          )
        );
      } else {
        [boundNode, beforeBoundNode] = take(
          this.#input.fetch({
            start: {
              row: takeState.bound,
              basis: "at"
            },
            constraint,
            reverse: true
          }),
          2
        );
      }
      const removeChange = {
        type: "remove",
        node: boundNode
      };
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        beforeBoundNode === void 0 || compareRows(change.node.row, beforeBoundNode.row) > 0 ? change.node.row : beforeBoundNode.row,
        maxBound
      );
      this.#withRowHiddenFromFetch(change.node.row, () => {
        this.#output.push(removeChange);
      });
      this.#output.push(change);
    } else if (change.type === "remove") {
      if (takeState.bound === void 0) {
        return;
      }
      const compToBound = compareRows(change.node.row, takeState.bound);
      if (compToBound > 0) {
        return;
      }
      const [beforeBoundNode] = take(
        this.#input.fetch({
          start: {
            row: takeState.bound,
            basis: "after"
          },
          constraint,
          reverse: true
        }),
        1
      );
      let newBound;
      if (beforeBoundNode) {
        const push = compareRows(beforeBoundNode.row, takeState.bound) > 0;
        newBound = {
          node: beforeBoundNode,
          push
        };
      }
      if (!newBound?.push) {
        for (const node of this.#input.fetch({
          start: {
            row: takeState.bound,
            basis: "at"
          },
          constraint
        })) {
          const push = compareRows(node.row, takeState.bound) > 0;
          newBound = {
            node,
            push
          };
          if (push) {
            break;
          }
        }
      }
      if (newBound?.push) {
        this.#output.push(change);
        this.#setTakeState(
          takeStateKey,
          takeState.size,
          newBound.node.row,
          maxBound
        );
        this.#output.push({
          type: "add",
          node: newBound.node
        });
        return;
      }
      this.#setTakeState(
        takeStateKey,
        takeState.size - 1,
        newBound?.node.row,
        maxBound
      );
      this.#output.push(change);
    } else if (change.type === "child") {
      if (takeState.bound && compareRows(change.node.row, takeState.bound) <= 0) {
        this.#output.push(change);
      }
    }
  }
  #pushEditChange(change) {
    assert(
      !this.#partitionKeyComparator || this.#partitionKeyComparator(change.oldNode.row, change.node.row) === 0,
      "Unexpected change of partition key"
    );
    const { takeState, takeStateKey, maxBound, constraint } = this.#getStateAndConstraint(change.oldNode.row);
    if (!takeState) {
      return;
    }
    assert(takeState.bound, "Bound should be set");
    const { compareRows } = this.getSchema();
    const oldCmp = compareRows(change.oldNode.row, takeState.bound);
    const newCmp = compareRows(change.node.row, takeState.bound);
    const replaceBoundAndForwardChange = () => {
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        change.node.row,
        maxBound
      );
      this.#output.push(change);
    };
    if (oldCmp === 0) {
      if (newCmp === 0) {
        this.#output.push(change);
        return;
      }
      if (newCmp < 0) {
        if (this.#limit === 1) {
          replaceBoundAndForwardChange();
          return;
        }
        const beforeBoundNode = must(
          first(
            this.#input.fetch({
              start: {
                row: takeState.bound,
                basis: "after"
              },
              constraint,
              reverse: true
            })
          )
        );
        this.#setTakeState(
          takeStateKey,
          takeState.size,
          beforeBoundNode.row,
          maxBound
        );
        this.#output.push(change);
        return;
      }
      assert(newCmp > 0);
      const newBoundNode = must(
        first(
          this.#input.fetch({
            start: {
              row: takeState.bound,
              basis: "at"
            },
            constraint
          })
        )
      );
      if (compareRows(newBoundNode.row, change.node.row) === 0) {
        replaceBoundAndForwardChange();
        return;
      }
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        newBoundNode.row,
        maxBound
      );
      this.#output.push({
        type: "add",
        node: newBoundNode
      });
      return;
    }
    if (oldCmp > 0) {
      assert(newCmp !== 0, "Invalid state. Row has duplicate primary key");
      if (newCmp > 0) {
        return;
      }
      assert(newCmp < 0);
      const [oldBoundNode, newBoundNode] = take(
        this.#input.fetch({
          start: {
            row: takeState.bound,
            basis: "at"
          },
          constraint,
          reverse: true
        }),
        2
      );
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        newBoundNode.row,
        maxBound
      );
      this.#withRowHiddenFromFetch(change.node.row, () => {
        this.#output.push({
          type: "remove",
          node: oldBoundNode
        });
      });
      this.#output.push({
        type: "add",
        node: change.node
      });
      return;
    }
    if (oldCmp < 0) {
      assert(newCmp !== 0, "Invalid state. Row has duplicate primary key");
      if (newCmp < 0) {
        this.#output.push(change);
        return;
      }
      assert(newCmp > 0);
      const afterBoundNode = must(
        first(
          this.#input.fetch({
            start: {
              row: takeState.bound,
              basis: "after"
            },
            constraint
          })
        )
      );
      if (compareRows(afterBoundNode.row, change.node.row) === 0) {
        replaceBoundAndForwardChange();
        return;
      }
      this.#output.push({
        type: "remove",
        node: change.oldNode
      });
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        afterBoundNode.row,
        maxBound
      );
      this.#output.push({
        type: "add",
        node: afterBoundNode
      });
      return;
    }
    unreachable();
  }
  #withRowHiddenFromFetch(row, fn) {
    this.#rowHiddenFromFetch = row;
    try {
      fn();
    } finally {
      this.#rowHiddenFromFetch = void 0;
    }
  }
  #setTakeState(takeStateKey, size, bound, maxBound) {
    this.#storage.set(takeStateKey, {
      size,
      bound
    });
    if (bound !== void 0 && (maxBound === void 0 || this.getSchema().compareRows(bound, maxBound) > 0)) {
      this.#storage.set(MAX_BOUND_KEY, bound);
    }
  }
  destroy() {
    this.#input.destroy();
  }
};
function getTakeStateKey(partitionKey, rowOrConstraint) {
  const partitionValues = [];
  if (partitionKey && rowOrConstraint) {
    for (const key of partitionKey) {
      partitionValues.push(rowOrConstraint[key]);
    }
  }
  return JSON.stringify(["take", ...partitionValues]);
}
function constraintMatchesPartitionKey(constraint, partitionKey) {
  if (constraint === void 0 || partitionKey === void 0) {
    return constraint === partitionKey;
  }
  if (partitionKey.length !== Object.keys(constraint).length) {
    return false;
  }
  for (const key of partitionKey) {
    if (!hasOwn(constraint, key)) {
      return false;
    }
  }
  return true;
}
function makePartitionKeyComparator(partitionKey) {
  return (a, b) => {
    for (const key of partitionKey) {
      const cmp2 = compareValues(a[key], b[key]);
      if (cmp2 !== 0) {
        return cmp2;
      }
    }
    return 0;
  };
}

// ../zql/src/builder/like.ts
function getLikePredicate(pattern, flags) {
  const op = getLikeOp(String(pattern), flags);
  return (lhs) => {
    assertString(lhs);
    return op(String(lhs));
  };
}
function getLikeOp(pattern, flags) {
  if (!/_|%|\\/.test(pattern)) {
    if (flags === "i") {
      const rhsLower = pattern.toLowerCase();
      return (lhs) => lhs.toLowerCase() === rhsLower;
    }
    return (lhs) => lhs === pattern;
  }
  const re = patternToRegExp(pattern, flags);
  return (lhs) => re.test(lhs);
}
var specialCharsRe = /[$()*+.?[\]\\^{|}]/;
function patternToRegExp(source, flags = "") {
  let pattern = "^";
  for (let i = 0; i < source.length; i++) {
    let c = source[i];
    switch (c) {
      case "%":
        pattern += ".*";
        break;
      case "_":
        pattern += ".";
        break;
      // @ts-expect-error fallthrough
      case "\\":
        if (i === source.length - 1) {
          throw new Error("LIKE pattern must not end with escape character");
        }
        i++;
        c = source[i];
      // fall through
      default:
        if (specialCharsRe.test(c)) {
          pattern += "\\";
        }
        pattern += c;
        break;
    }
  }
  return new RegExp(pattern + "$", flags + "m");
}

// ../zql/src/builder/filter.ts
function createPredicate(condition) {
  if (condition.type !== "simple") {
    const predicates = condition.conditions.map((c) => createPredicate(c));
    return condition.type === "and" ? (row) => {
      for (const predicate of predicates) {
        if (!predicate(row)) {
          return false;
        }
      }
      return true;
    } : (row) => {
      for (const predicate of predicates) {
        if (predicate(row)) {
          return true;
        }
      }
      return false;
    };
  }
  const { left } = condition;
  const { right } = condition;
  assert(
    right.type !== "static",
    "static values should be resolved before creating predicates"
  );
  assert(
    left.type !== "static",
    "static values should be resolved before creating predicates"
  );
  switch (condition.op) {
    case "IS":
    case "IS NOT": {
      const impl2 = createIsPredicate(right.value, condition.op);
      if (left.type === "literal") {
        const result = impl2(left.value);
        return () => result;
      }
      return (row) => impl2(row[left.name]);
    }
  }
  if (right.value === null || right.value === void 0) {
    return (_row) => false;
  }
  const impl = createPredicateImpl(right.value, condition.op);
  if (left.type === "literal") {
    if (left.value === null || left.value === void 0) {
      return (_row) => false;
    }
    const result = impl(left.value);
    return () => result;
  }
  return (row) => {
    const lhs = row[left.name];
    if (lhs === null || lhs === void 0) {
      return false;
    }
    return impl(lhs);
  };
}
function createIsPredicate(rhs, operator) {
  switch (operator) {
    case "IS":
      return (lhs) => lhs === rhs;
    case "IS NOT":
      return (lhs) => lhs !== rhs;
  }
}
function createPredicateImpl(rhs, operator) {
  switch (operator) {
    case "=":
      return (lhs) => lhs === rhs;
    case "!=":
      return (lhs) => lhs !== rhs;
    case "<":
      return (lhs) => lhs < rhs;
    case "<=":
      return (lhs) => lhs <= rhs;
    case ">":
      return (lhs) => lhs > rhs;
    case ">=":
      return (lhs) => lhs >= rhs;
    case "LIKE":
      return getLikePredicate(rhs, "");
    case "NOT LIKE":
      return not(getLikePredicate(rhs, ""));
    case "ILIKE":
      return getLikePredicate(rhs, "i");
    case "NOT ILIKE":
      return not(getLikePredicate(rhs, "i"));
    case "IN": {
      assert(Array.isArray(rhs));
      const set = new Set(rhs);
      return (lhs) => set.has(lhs);
    }
    case "NOT IN": {
      assert(Array.isArray(rhs));
      const set = new Set(rhs);
      return (lhs) => !set.has(lhs);
    }
    default:
      operator;
      throw new Error(`Unexpected operator: ${operator}`);
  }
}
function not(f) {
  return (lhs) => !f(lhs);
}
function transformFilters(filters) {
  if (!filters) {
    return { filters: void 0, conditionsRemoved: false };
  }
  switch (filters.type) {
    case "simple":
      return { filters, conditionsRemoved: false };
    case "correlatedSubquery":
      return { filters: void 0, conditionsRemoved: true };
    case "and": {
      const transformedConditions = [];
      for (const cond of filters.conditions) {
        assert(cond.type === "simple" || cond.type === "correlatedSubquery");
        if (cond.type === "simple") {
          transformedConditions.push(cond);
        }
      }
      const conditionsRemoved = transformedConditions.length !== filters.conditions.length;
      if (transformedConditions.length === 0) {
        return { filters: void 0, conditionsRemoved };
      }
      if (transformedConditions.length === 1) {
        return {
          filters: transformedConditions[0],
          conditionsRemoved
        };
      }
      return {
        filters: {
          type: "and",
          conditions: transformedConditions
        },
        conditionsRemoved
      };
    }
    case "or": {
      const transformedConditions = [];
      let conditionsRemoved = false;
      for (const cond of filters.conditions) {
        assert(cond.type !== "or");
        const transformed = transformFilters(cond);
        if (transformed.filters === void 0) {
          return { filters: void 0, conditionsRemoved: true };
        }
        conditionsRemoved = conditionsRemoved || transformed.conditionsRemoved;
        transformedConditions.push(transformed.filters);
      }
      return {
        filters: { type: "or", conditions: transformedConditions },
        conditionsRemoved
      };
    }
    default:
      unreachable(filters);
  }
}

// ../zql/src/builder/builder.ts
function buildPipeline(ast, delegate) {
  return buildPipelineInternal(delegate.mapAst(ast), delegate, "");
}
function buildPipelineInternal(ast, delegate, name, partitionKey) {
  const source = delegate.getSource(ast.table);
  if (!source) {
    throw new Error(`Source not found: ${ast.table}`);
  }
  ast = uniquifyCorrelatedSubqueryConditionAliases(ast);
  const csqsFromCondition = gatherCorrelatedSubqueryQueriesFromCondition(
    ast.where
  );
  const splitEditKeys = partitionKey ? new Set(partitionKey) : /* @__PURE__ */ new Set();
  for (const csq of csqsFromCondition) {
    for (const key of csq.correlation.parentField) {
      splitEditKeys.add(key);
    }
  }
  if (ast.related) {
    for (const csq of ast.related) {
      for (const key of csq.correlation.parentField) {
        splitEditKeys.add(key);
      }
    }
  }
  const conn = source.connect(must(ast.orderBy), ast.where, splitEditKeys);
  let end = delegate.decorateInput(conn, `${name}:source(${ast.table})`);
  const { fullyAppliedFilters } = conn;
  if (ast.start) {
    end = delegate.decorateInput(new Skip(end, ast.start), `${name}:skip)`);
  }
  for (const csq of csqsFromCondition) {
    end = applyCorrelatedSubQuery(csq, delegate, end, name);
  }
  if (ast.where && !fullyAppliedFilters) {
    end = applyWhere(end, ast.where, delegate, name);
  }
  if (ast.limit !== void 0) {
    const takeName = `${name}:take`;
    end = delegate.decorateInput(
      new Take(end, delegate.createStorage(takeName), ast.limit, partitionKey),
      takeName
    );
  }
  if (ast.related) {
    for (const csq of ast.related) {
      end = applyCorrelatedSubQuery(csq, delegate, end, name);
    }
  }
  return end;
}
function applyWhere(input, condition, delegate, name) {
  switch (condition.type) {
    case "and":
      return applyAnd(input, condition, delegate, name);
    case "or":
      return applyOr(input, condition, delegate, name);
    case "correlatedSubquery":
      return applyCorrelatedSubqueryCondition(input, condition, delegate, name);
    case "simple":
      return applySimpleCondition(input, condition);
  }
}
function applyAnd(input, condition, delegate, name) {
  for (const subCondition of condition.conditions) {
    input = applyWhere(input, subCondition, delegate, name);
  }
  return input;
}
function applyOr(input, condition, delegate, name) {
  const [subqueryConditions, otherConditions] = groupSubqueryConditions(condition);
  if (subqueryConditions.length === 0) {
    return new Filter(
      input,
      createPredicate({
        type: "or",
        conditions: otherConditions
      })
    );
  }
  const fanOut = new FanOut(input);
  const branches = subqueryConditions.map(
    (subCondition) => applyWhere(fanOut, subCondition, delegate, name)
  );
  if (otherConditions.length > 0) {
    branches.push(
      new Filter(
        fanOut,
        createPredicate({
          type: "or",
          conditions: otherConditions
        })
      )
    );
  }
  const ret = new FanIn(fanOut, branches);
  fanOut.setFanIn(ret);
  return ret;
}
function groupSubqueryConditions(condition) {
  const partitioned = [[], []];
  for (const subCondition of condition.conditions) {
    if (isNotAndDoesNotContainSubquery(subCondition)) {
      partitioned[1].push(subCondition);
    } else {
      partitioned[0].push(subCondition);
    }
  }
  return partitioned;
}
function isNotAndDoesNotContainSubquery(condition) {
  if (condition.type === "correlatedSubquery") {
    return false;
  }
  if (condition.type === "and") {
    return condition.conditions.every(isNotAndDoesNotContainSubquery);
  }
  assert(condition.type !== "or", "where conditions are expected to be in DNF");
  return true;
}
function applySimpleCondition(input, condition) {
  return new Filter(input, createPredicate(condition));
}
function applyCorrelatedSubQuery(sq, delegate, end, name) {
  assert(sq.subquery.alias, "Subquery must have an alias");
  const child = buildPipelineInternal(
    sq.subquery,
    delegate,
    `${name}.${sq.subquery.alias}`,
    sq.correlation.childField
  );
  const joinName = `${name}:join(${sq.subquery.alias})`;
  end = new Join({
    parent: end,
    child,
    storage: delegate.createStorage(joinName),
    parentKey: sq.correlation.parentField,
    childKey: sq.correlation.childField,
    relationshipName: sq.subquery.alias,
    hidden: sq.hidden ?? false,
    system: sq.system ?? "client"
  });
  return delegate.decorateInput(end, joinName);
}
function applyCorrelatedSubqueryCondition(input, condition, delegate, name) {
  assert(condition.op === "EXISTS" || condition.op === "NOT EXISTS");
  const existsName = `${name}:exists(${condition.related.subquery.alias})`;
  return delegate.decorateInput(
    new Exists(
      input,
      delegate.createStorage(existsName),
      must(condition.related.subquery.alias),
      condition.related.correlation.parentField,
      condition.op
    ),
    existsName
  );
}
function gatherCorrelatedSubqueryQueriesFromCondition(condition) {
  const csqs = [];
  const gather = (condition2) => {
    if (condition2.type === "correlatedSubquery") {
      assert(condition2.op === "EXISTS" || condition2.op === "NOT EXISTS");
      csqs.push({
        ...condition2.related,
        subquery: {
          ...condition2.related.subquery,
          limit: condition2.related.system === "permissions" ? PERMISSIONS_EXISTS_LIMIT : EXISTS_LIMIT
        }
      });
      return;
    }
    if (condition2.type === "and" || condition2.type === "or") {
      for (const c of condition2.conditions) {
        gather(c);
      }
      return;
    }
  };
  if (condition) {
    gather(condition);
  }
  return csqs;
}
var EXISTS_LIMIT = 3;
var PERMISSIONS_EXISTS_LIMIT = 1;
function assertOrderingIncludesPK(ordering, pk) {
  const orderingFields = ordering.map(([field]) => field);
  const missingFields = pk.filter((pkField) => !orderingFields.includes(pkField));
  if (missingFields.length > 0) {
    throw new Error(
      `Ordering must include all primary key fields. Missing: ${missingFields.join(
        ", "
      )}. ZQL automatically appends primary key fields to the ordering if they are missing 
      so a common cause of this error is a casing mismatch between Postgres and ZQL.
      E.g., "userid" vs "userID".
      You may want to add double-quotes around your Postgres column names to prevent Postgres from lower-casing them:
      https://www.postgresql.org/docs/current/sql-syntax-lexical.htm`
    );
  }
}
function uniquifyCorrelatedSubqueryConditionAliases(ast) {
  if (!ast.where) {
    return ast;
  }
  const { where } = ast;
  if (where.type !== "and" && where.type !== "or") {
    return ast;
  }
  let count = 0;
  const uniquifyCorrelatedSubquery = (csqc) => ({
    ...csqc,
    related: {
      ...csqc.related,
      subquery: {
        ...csqc.related.subquery,
        alias: (csqc.related.subquery.alias ?? "") + "_" + count++
      }
    }
  });
  const uniquifyAnd = (and2) => {
    const conds2 = [];
    for (const cond of and2.conditions) {
      if (cond.type === "correlatedSubquery") {
        conds2.push(uniquifyCorrelatedSubquery(cond));
      } else {
        conds2.push(cond);
      }
    }
    return {
      ...and2,
      conditions: conds2
    };
  };
  if (where.type === "and") {
    return {
      ...ast,
      where: uniquifyAnd(where)
    };
  }
  const conds = [];
  for (const cond of where.conditions) {
    if (cond.type === "simple") {
      conds.push(cond);
    } else if (cond.type === "correlatedSubquery") {
      conds.push(uniquifyCorrelatedSubquery(cond));
    } else if (cond.type === "and") {
      conds.push(uniquifyAnd(cond));
    }
  }
  return {
    ...ast,
    where: {
      ...where,
      conditions: conds
    }
  };
}

// ../zql/src/ivm/array-view.ts
var ArrayView = class {
  #input;
  #listeners = /* @__PURE__ */ new Set();
  #schema;
  #format;
  // Synthetic "root" entry that has a single "" relationship, so that we can
  // treat all changes, including the root change, generically.
  #root;
  onDestroy;
  #dirty = false;
  #complete = false;
  constructor(input, format = { singular: false, relationships: {} }, queryComplete = true) {
    this.#input = input;
    this.#schema = input.getSchema();
    this.#format = format;
    this.#root = { "": format.singular ? void 0 : [] };
    input.setOutput(this);
    if (queryComplete === true) {
      this.#complete = true;
    } else {
      void queryComplete.then(() => {
        this.#complete = true;
        this.#fireListeners();
      });
    }
    this.#hydrate();
  }
  get data() {
    return this.#root[""];
  }
  addListener(listener) {
    assert(!this.#listeners.has(listener), "Listener already registered");
    this.#listeners.add(listener);
    this.#fireListener(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }
  #fireListeners() {
    for (const listener of this.#listeners) {
      this.#fireListener(listener);
    }
  }
  #fireListener(listener) {
    listener(
      this.data,
      this.#complete ? "complete" : "unknown"
    );
  }
  destroy() {
    this.onDestroy?.();
  }
  #hydrate() {
    this.#dirty = true;
    for (const node of this.#input.fetch({})) {
      applyChange(
        this.#root,
        { type: "add", node },
        this.#schema,
        "",
        this.#format
      );
    }
    this.flush();
  }
  push(change) {
    this.#dirty = true;
    applyChange(this.#root, change, this.#schema, "", this.#format);
  }
  flush() {
    if (!this.#dirty) {
      return;
    }
    this.#dirty = false;
    this.#fireListeners();
  }
};

// ../zql/src/query/expression.ts
var ExpressionBuilder = class {
  #exists;
  constructor(exists) {
    this.#exists = exists;
    this.exists = this.exists.bind(this);
  }
  get eb() {
    return this;
  }
  cmp(field, opOrValue, value) {
    return cmp(field, opOrValue, value);
  }
  cmpLit(left, op, right) {
    return {
      type: "simple",
      left: isParameterReference(left) ? left[toStaticParam]() : { type: "literal", value: left },
      right: isParameterReference(right) ? right[toStaticParam]() : { type: "literal", value: right },
      op
    };
  }
  and = and;
  or = or;
  not = not2;
  exists = (relationship, cb) => this.#exists(relationship, cb);
};
function and(...conditions) {
  const expressions = filterTrue(filterUndefined(conditions));
  if (expressions.length === 1) {
    return expressions[0];
  }
  if (expressions.some(isAlwaysFalse)) {
    return FALSE;
  }
  return { type: "and", conditions: expressions };
}
function or(...conditions) {
  const expressions = filterFalse(filterUndefined(conditions));
  if (expressions.length === 1) {
    return expressions[0];
  }
  if (expressions.some(isAlwaysTrue)) {
    return TRUE;
  }
  return { type: "or", conditions: expressions };
}
function not2(expression) {
  switch (expression.type) {
    case "and":
      return {
        type: "or",
        conditions: expression.conditions.map(not2)
      };
    case "or":
      return {
        type: "and",
        conditions: expression.conditions.map(not2)
      };
    case "correlatedSubquery":
      return {
        type: "correlatedSubquery",
        related: expression.related,
        op: negateOperator(expression.op)
      };
    case "simple":
      return {
        type: "simple",
        op: negateOperator(expression.op),
        left: expression.left,
        right: expression.right
      };
  }
}
function cmp(field, opOrValue, value) {
  let op;
  if (value === void 0) {
    value = opOrValue;
    op = "=";
  } else {
    op = opOrValue;
  }
  return {
    type: "simple",
    left: { type: "column", name: field },
    right: isParameterReference(value) ? value[toStaticParam]() : { type: "literal", value },
    op
  };
}
function isParameterReference(value) {
  return value !== null && typeof value === "object" && value[toStaticParam];
}
var TRUE = {
  type: "and",
  conditions: []
};
var FALSE = {
  type: "or",
  conditions: []
};
function isAlwaysTrue(condition) {
  return condition.type === "and" && condition.conditions.length === 0;
}
function isAlwaysFalse(condition) {
  return condition.type === "or" && condition.conditions.length === 0;
}
function flatten(type, conditions) {
  const flattened2 = [];
  for (const c of conditions) {
    if (c.type === type) {
      flattened2.push(...c.conditions);
    } else {
      flattened2.push(c);
    }
  }
  return flattened2;
}
var negateSimpleOperatorMap = {
  ["="]: "!=",
  ["!="]: "=",
  ["<"]: ">=",
  [">"]: "<=",
  [">="]: "<",
  ["<="]: ">",
  ["IN"]: "NOT IN",
  ["NOT IN"]: "IN",
  ["LIKE"]: "NOT LIKE",
  ["NOT LIKE"]: "LIKE",
  ["ILIKE"]: "NOT ILIKE",
  ["NOT ILIKE"]: "ILIKE",
  ["IS"]: "IS NOT",
  ["IS NOT"]: "IS"
};
var negateOperatorMap = {
  ...negateSimpleOperatorMap,
  ["EXISTS"]: "NOT EXISTS",
  ["NOT EXISTS"]: "EXISTS"
};
function negateOperator(op) {
  return must(negateOperatorMap[op]);
}
function filterUndefined(array5) {
  return array5.filter((e) => e !== void 0);
}
function filterTrue(conditions) {
  return conditions.filter((c) => !isAlwaysTrue(c));
}
function filterFalse(conditions) {
  return conditions.filter((c) => !isAlwaysFalse(c));
}

// ../zql/src/query/dnf.ts
function dnf(condition) {
  return unwrap(dnfInner(condition));
}
function dnfInner(condition) {
  switch (condition.type) {
    case "simple":
    case "correlatedSubquery":
      return { type: "or", conditions: [condition] };
    case "and":
      return distributeAnd(condition.conditions.map(dnfInner));
    case "or":
      return {
        type: "or",
        conditions: flatten(
          "or",
          condition.conditions.map(dnfInner).flatMap((c) => c.conditions)
        )
      };
    default:
      unreachable(condition);
  }
}
function distributeAnd(conditions) {
  if (conditions.length === 0) {
    return { type: "or", conditions: [TRUE] };
  }
  return conditions.reduce((acc, orCondition) => {
    const newConditions = [];
    for (const accCondition of acc.conditions) {
      for (const orSubCondition of orCondition.conditions) {
        newConditions.push({
          type: "and",
          conditions: [accCondition, orSubCondition]
        });
      }
    }
    return {
      type: "or",
      conditions: flatten("or", newConditions)
    };
  });
}
function unwrap(c) {
  if (c.type === "simple" || c.type === "correlatedSubquery") {
    return c;
  }
  if (c.conditions.length === 1) {
    return unwrap(c.conditions[0]);
  }
  return { type: c.type, conditions: flatten(c.type, c.conditions.map(unwrap)) };
}

// ../zql/src/query/query-impl.ts
var astForTestingSymbol = Symbol();
function newQuery(delegate, schema, table) {
  return new QueryImpl(delegate, schema, table, { table }, defaultFormat);
}
function newQueryWithDetails(delegate, schema, tableName, ast, format) {
  return new QueryImpl(delegate, schema, tableName, ast, format);
}
function staticParam(anchorClass, field) {
  return {
    type: "static",
    anchor: anchorClass,
    // for backwards compatibility
    field: field.length === 1 ? field[0] : field
  };
}
var SUBQ_PREFIX = "zsubq_";
var defaultFormat = { singular: false, relationships: {} };
var AbstractQuery = class {
  #schema;
  #tableName;
  #ast;
  format;
  #hash = "";
  constructor(schema, tableName, ast, format) {
    this.#schema = schema;
    this.#tableName = tableName;
    this.#ast = ast;
    this.format = format;
  }
  // Not part of Query or QueryInternal interface
  get [astForTestingSymbol]() {
    return this.#ast;
  }
  hash() {
    if (!this.#hash) {
      this.#hash = hashOfAST(this._completeAst());
    }
    return this.#hash;
  }
  one() {
    return this._newQuery(
      this.#schema,
      this.#tableName,
      {
        ...this.#ast,
        limit: 1
      },
      {
        ...this.format,
        singular: true
      }
    );
  }
  whereExists(relationship, cb) {
    return this.where(({ exists }) => exists(relationship, cb));
  }
  related(relationship, cb) {
    if (relationship.startsWith(SUBQ_PREFIX)) {
      throw new Error(
        `Relationship names may not start with "${SUBQ_PREFIX}". That is a reserved prefix.`
      );
    }
    cb = cb ?? ((q) => q);
    const related = this.#schema.relationships[this.#tableName][relationship];
    assert(related, "Invalid relationship");
    if (isOneHop(related)) {
      const { destSchema, destField, sourceField, cardinality } = related[0];
      let q = this._newQuery(
        this.#schema,
        destSchema,
        {
          table: destSchema,
          alias: relationship
        },
        {
          relationships: {},
          singular: cardinality === "one"
        }
      );
      if (cardinality === "one") {
        q = q.one();
      }
      const sq = cb(q);
      assert(
        isCompoundKey(sourceField),
        "The source of a relationship must specify at last 1 field"
      );
      assert(
        isCompoundKey(destField),
        "The destination of a relationship must specify at last 1 field"
      );
      assert(
        sourceField.length === destField.length,
        "The source and destination of a relationship must have the same number of fields"
      );
      return this._newQuery(
        this.#schema,
        this.#tableName,
        {
          ...this.#ast,
          related: [
            ...this.#ast.related ?? [],
            {
              system: this._system,
              correlation: {
                parentField: sourceField,
                childField: destField
              },
              subquery: addPrimaryKeysToAst(
                this.#schema.tables[destSchema],
                sq.#ast
              )
            }
          ]
        },
        {
          ...this.format,
          relationships: {
            ...this.format.relationships,
            [relationship]: sq.format
          }
        }
      );
    }
    if (isTwoHop(related)) {
      const [firstRelation, secondRelation] = related;
      const { destSchema } = secondRelation;
      const junctionSchema = firstRelation.destSchema;
      const sq = cb(
        this._newQuery(
          this.#schema,
          destSchema,
          {
            table: destSchema,
            alias: relationship
          },
          {
            relationships: {},
            singular: secondRelation.cardinality === "one"
          }
        )
      );
      assert(isCompoundKey(firstRelation.sourceField), "Invalid relationship");
      assert(isCompoundKey(firstRelation.destField), "Invalid relationship");
      assert(isCompoundKey(secondRelation.sourceField), "Invalid relationship");
      assert(isCompoundKey(secondRelation.destField), "Invalid relationship");
      return this._newQuery(
        this.#schema,
        this.#tableName,
        {
          ...this.#ast,
          related: [
            ...this.#ast.related ?? [],
            {
              system: this._system,
              correlation: {
                parentField: firstRelation.sourceField,
                childField: firstRelation.destField
              },
              hidden: true,
              subquery: {
                table: junctionSchema,
                alias: relationship,
                orderBy: addPrimaryKeys(
                  this.#schema.tables[junctionSchema],
                  void 0
                ),
                related: [
                  {
                    system: this._system,
                    correlation: {
                      parentField: secondRelation.sourceField,
                      childField: secondRelation.destField
                    },
                    subquery: addPrimaryKeysToAst(
                      this.#schema.tables[destSchema],
                      sq.#ast
                    )
                  }
                ]
              }
            }
          ]
        },
        {
          ...this.format,
          relationships: {
            ...this.format.relationships,
            [relationship]: sq.format
          }
        }
      );
    }
    throw new Error(`Invalid relationship ${relationship}`);
  }
  where(fieldOrExpressionFactory, opOrValue, value) {
    let cond;
    if (typeof fieldOrExpressionFactory === "function") {
      cond = fieldOrExpressionFactory(
        new ExpressionBuilder(this._exists)
      );
    } else {
      assert(opOrValue !== void 0, "Invalid condition");
      cond = cmp(fieldOrExpressionFactory, opOrValue, value);
    }
    const existingWhere = this.#ast.where;
    if (existingWhere) {
      cond = and(existingWhere, cond);
    }
    return this._newQuery(
      this.#schema,
      this.#tableName,
      {
        ...this.#ast,
        where: dnf(cond)
      },
      this.format
    );
  }
  start(row, opts) {
    return this._newQuery(
      this.#schema,
      this.#tableName,
      {
        ...this.#ast,
        start: {
          row,
          exclusive: !opts?.inclusive
        }
      },
      this.format
    );
  }
  limit(limit) {
    if (limit < 0) {
      throw new Error("Limit must be non-negative");
    }
    if ((limit | 0) !== limit) {
      throw new Error("Limit must be an integer");
    }
    return this._newQuery(
      this.#schema,
      this.#tableName,
      {
        ...this.#ast,
        limit
      },
      this.format
    );
  }
  orderBy(field, direction) {
    return this._newQuery(
      this.#schema,
      this.#tableName,
      {
        ...this.#ast,
        orderBy: [...this.#ast.orderBy ?? [], [field, direction]]
      },
      this.format
    );
  }
  _exists = (relationship, cb = (q) => q) => {
    const related = this.#schema.relationships[this.#tableName][relationship];
    assert(related, "Invalid relationship");
    if (isOneHop(related)) {
      const { destSchema, sourceField, destField } = related[0];
      assert(isCompoundKey(sourceField), "Invalid relationship");
      assert(isCompoundKey(destField), "Invalid relationship");
      const sq = cb(
        this._newQuery(
          this.#schema,
          destSchema,
          {
            table: destSchema,
            alias: `${SUBQ_PREFIX}${relationship}`
          },
          defaultFormat
        )
      );
      return {
        type: "correlatedSubquery",
        related: {
          system: this._system,
          correlation: {
            parentField: sourceField,
            childField: destField
          },
          subquery: addPrimaryKeysToAst(
            this.#schema.tables[destSchema],
            sq.#ast
          )
        },
        op: "EXISTS"
      };
    }
    if (isTwoHop(related)) {
      const [firstRelation, secondRelation] = related;
      assert(isCompoundKey(firstRelation.sourceField), "Invalid relationship");
      assert(isCompoundKey(firstRelation.destField), "Invalid relationship");
      assert(isCompoundKey(secondRelation.sourceField), "Invalid relationship");
      assert(isCompoundKey(secondRelation.destField), "Invalid relationship");
      const { destSchema } = secondRelation;
      const junctionSchema = firstRelation.destSchema;
      const queryToDest = cb(
        this._newQuery(
          this.#schema,
          destSchema,
          {
            table: destSchema,
            alias: `${SUBQ_PREFIX}${relationship}`
          },
          defaultFormat
        )
      );
      return {
        type: "correlatedSubquery",
        related: {
          system: this._system,
          correlation: {
            parentField: firstRelation.sourceField,
            childField: firstRelation.destField
          },
          subquery: {
            table: junctionSchema,
            alias: `${SUBQ_PREFIX}${relationship}`,
            orderBy: addPrimaryKeys(
              this.#schema.tables[junctionSchema],
              void 0
            ),
            where: {
              type: "correlatedSubquery",
              related: {
                system: this._system,
                correlation: {
                  parentField: secondRelation.sourceField,
                  childField: secondRelation.destField
                },
                subquery: addPrimaryKeysToAst(
                  this.#schema.tables[destSchema],
                  queryToDest.#ast
                )
              },
              op: "EXISTS"
            }
          }
        },
        op: "EXISTS"
      };
    }
    throw new Error(`Invalid relationship ${relationship}`);
  };
  #completedAST;
  _completeAst() {
    if (!this.#completedAST) {
      const finalOrderBy = addPrimaryKeys(
        this.#schema.tables[this.#tableName],
        this.#ast.orderBy
      );
      if (this.#ast.start) {
        const { row } = this.#ast.start;
        const narrowedRow = {};
        for (const [field] of finalOrderBy) {
          narrowedRow[field] = row[field];
        }
        this.#completedAST = {
          ...this.#ast,
          start: {
            ...this.#ast.start,
            row: narrowedRow
          },
          orderBy: finalOrderBy
        };
      } else {
        this.#completedAST = {
          ...this.#ast,
          orderBy: addPrimaryKeys(
            this.#schema.tables[this.#tableName],
            this.#ast.orderBy
          )
        };
      }
    }
    return this.#completedAST;
  }
};
var completedAstSymbol = Symbol();
var QueryImpl = class extends AbstractQuery {
  #delegate;
  constructor(delegate, schema, tableName, ast, format) {
    super(schema, tableName, ast, format);
    this.#delegate = delegate;
  }
  _system = "client";
  get [completedAstSymbol]() {
    return this._completeAst();
  }
  _newQuery(schema, tableName, ast, format) {
    return newQueryWithDetails(this.#delegate, schema, tableName, ast, format);
  }
  materialize(factoryOrTTL, ttl = DEFAULT_TTL) {
    const t0 = Date.now();
    let factory;
    if (typeof factoryOrTTL === "function") {
      factory = factoryOrTTL;
    } else {
      ttl = factoryOrTTL ?? DEFAULT_TTL;
    }
    const ast = this._completeAst();
    const queryCompleteResolver = resolver();
    let queryGot = false;
    const removeServerQuery = this.#delegate.addServerQuery(ast, ttl, (got) => {
      if (got) {
        const t1 = Date.now();
        this.#delegate.onQueryMaterialized(this.hash(), ast, t1 - t0);
        queryGot = true;
        queryCompleteResolver.resolve(true);
      }
    });
    const input = buildPipeline(ast, this.#delegate);
    let removeCommitObserver;
    const onDestroy = () => {
      input.destroy();
      removeCommitObserver?.();
      removeServerQuery();
    };
    const view = this.#delegate.batchViewUpdates(
      () => (factory ?? arrayViewFactory)(
        this,
        input,
        this.format,
        onDestroy,
        (cb) => {
          removeCommitObserver = this.#delegate.onTransactionCommit(cb);
        },
        queryGot || queryCompleteResolver.promise
      )
    );
    return view;
  }
  updateTTL(ttl) {
    this.#delegate.updateServerQuery(this._completeAst(), ttl);
  }
  run() {
    const v2 = this.materialize();
    const ret = v2.data;
    v2.destroy();
    return Promise.resolve(ret);
  }
  preload(options) {
    const { resolve, promise: complete } = resolver();
    const ast = this._completeAst();
    const unsub = this.#delegate.addServerQuery(
      ast,
      options?.ttl ?? DEFAULT_TTL,
      (got) => {
        if (got) {
          resolve();
        }
      }
    );
    return {
      cleanup: unsub,
      complete
    };
  }
};
function addPrimaryKeys(schema, orderBy) {
  orderBy = orderBy ?? [];
  const { primaryKey } = schema;
  const primaryKeysToAdd = new Set(primaryKey);
  for (const [field] of orderBy) {
    primaryKeysToAdd.delete(field);
  }
  if (primaryKeysToAdd.size === 0) {
    return orderBy;
  }
  return [
    ...orderBy,
    ...[...primaryKeysToAdd].map((key) => [key, "asc"])
  ];
}
function addPrimaryKeysToAst(schema, ast) {
  return {
    ...ast,
    orderBy: addPrimaryKeys(schema, ast.orderBy)
  };
}
function arrayViewFactory(_query, input, format, onDestroy, onTransactionCommit, queryComplete) {
  const v2 = new ArrayView(input, format, queryComplete);
  v2.onDestroy = onDestroy;
  onTransactionCommit(() => {
    v2.flush();
  });
  return v2;
}
function isCompoundKey(field) {
  return Array.isArray(field) && field.length >= 1;
}

export {
  isProd,
  deepEqual,
  assertJSONValue,
  assertJSONObject,
  stringCompare,
  compareCookies,
  assertCookie,
  deepFreeze,
  deepFreezeAllowUndefined,
  parse,
  test,
  readonlyObject,
  readonlyArray,
  valita_exports,
  emptyHash,
  newRandomHash,
  assertHash,
  Chunk,
  assertRefs,
  createChunk,
  ChunkNotFoundError,
  mustGetChunk,
  mustGetHeadHash,
  DD31,
  V6,
  V7,
  Latest,
  joinIterables,
  wrapIterable,
  getSizeOfValue,
  DEFAULT_HEAD_NAME,
  commitIsLocalDD31,
  localMutations,
  localMutationsDD31,
  localMutationsGreaterThan,
  baseSnapshotFromHead,
  baseSnapshotFromHash,
  baseSnapshotFromCommit,
  snapshotMetaParts,
  compareCookiesForSnapshots,
  commitFromHash,
  commitFromHead,
  assertLocalMetaDD31,
  isLocalMetaDD31,
  assertSnapshotMetaDD31,
  assertSnapshotCommitDD31,
  binarySearch,
  BTreeRead,
  encodeIndexScanKey,
  decodeIndexKey,
  readFromDefaultHead,
  readFromHash,
  asyncIterableToArray,
  diff,
  DiffsMap,
  diff2,
  diffCommits,
  addDiffsForIndexes,
  newWriteLocal,
  newWriteSnapshotDD31,
  getClientGroups,
  setClientGroups,
  setClientGroup,
  getClientGroup,
  clientGroupHasPendingMutations,
  disableClientGroup,
  withRead,
  withWriteNoImplicitCommit,
  withWrite,
  using,
  jsonSchema,
  jsonObjectSchema,
  clientGroupIDSchema,
  clientIDSchema,
  makeClientID,
  assertClientV6,
  getClients,
  ClientStateNotFoundError,
  assertHasClientState,
  hasClientState,
  getClient,
  mustGetClient,
  initClientV6,
  getClientGroupForClient,
  getClientGroupIDForClient,
  setClient,
  setClients,
  getNonCryptoRandomValues,
  h64,
  rowSchema,
  toStaticParam,
  astSchema,
  normalizeAST,
  mapAST,
  mapCondition,
  hashOfAST,
  filterPush,
  createPredicate,
  transformFilters,
  assertOrderingIncludesPK,
  ExpressionBuilder,
  newQuery,
  staticParam,
  SUBQ_PREFIX,
  defaultFormat,
  AbstractQuery,
  inspectQueriesDownSchema,
  inspectDownMessageSchema,
  primaryKeySchema,
  primaryKeyValueRecordSchema,
  nanoid,
  GOT_QUERIES_KEY_PREFIX,
  ENTITIES_KEY_PREFIX,
  toDesiredQueriesKey,
  desiredQueriesPrefixForClient,
  toGotQueriesKey,
  toPrimaryKeyString,
  sourceNameFromKey
};
//# sourceMappingURL=chunk-S6IF5D2U.js.map
